/** What the generator is allowed to put where.
 *
 * Every id here is a `MapObjectDef` id, which is the `obj.*` atlas key with the
 * prefix stripped - so a table row is simultaneously the rule and the art.
 * Nothing in this file knows a footprint: that comes from the def, which is the
 * only place a footprint is written down.
 */

import type { ContentRegistry, CreatureDef, TerrainKind } from "@game/core";

/** Resource piles, in rough order of value. */
export const RESOURCE_PILES: readonly string[] = Object.freeze([
  "res_wood", "res_ore", "res_gold", "res_mercury", "res_sulfur", "res_crystal", "res_gems",
]);

export const CHEAP_PILES: readonly string[] = Object.freeze(["res_wood", "res_ore", "res_gold"]);

export const MINES: readonly string[] = Object.freeze([
  "mine_sawmill", "mine_ore", "mine_gold", "mine_crystal", "mine_gem",
  "mine_sulfur", "mine_alchemist", "mine_stone_quarry", "mine_obsidian_quarry",
  "mine_peat_bog", "mine_salt_flat",
]);

/** The two a starting neighbourhood must have, per the classic convention. */
export const STARTING_MINES: readonly string[] = Object.freeze(["mine_sawmill", "mine_ore"]);

/** @deprecated Scatter uses `scatterPool(content).dwellings`. Kept so the
 *  catalogue-id test still names every dwelling sprite that was drawn. */
export const DWELLINGS: readonly string[] = Object.freeze([
  "dwl_guardhouse", "dwl_cinder_forge", "dwl_hut", "dwl_glimmer_ring", "dwl_glade_stables",
  "dwl_bone_pit", "dwl_crypt", "dwl_fang_den", "dwl_shriek_cliff", "dwl_stonebeard_hall",
  "dwl_rook_roost", "dwl_sentinel_works", "dwl_grove", "dwl_treehouse",
  "dwl_serpent_pool", "dwl_tempter_gate", "dwl_watcher_sanctum",
  "dwl_raider_picket", "dwl_ancient_lamp", "bld_refugee_camp",
]);

/** @deprecated Scatter uses `scatterPool(content).banks`. */
export const BANKS: readonly string[] = Object.freeze([
  "bank_crypt", "bank_shipwreck", "bank_skyclaw_aerie", "bank_stonegaze_lair",
  "bank_derelict_tower", "bank_serpent_vault", "bank_wyrm_hoard",
  "bank_churchyard", "bank_ruins", "bank_smuggler_cave", "bank_mansion",
  "bank_red_tower", "bld_sand_tomb",
]);

/**
 * Adventure-map sprites a wandering stack can wear. A map-object def is a
 * sprite (mapgen.spec.ts: the id IS the art key), and only these nineteen
 * were drawn, so a Spearwarden stack on the map still stands on `mon_cinderling` or
 * `mon_bonewalker`. The creature in the payload is the fight; this table is
 * the picture.
 */
export const GUARDS: readonly string[] = Object.freeze([
  "mon_cinderling", "mon_scrapper", "mon_bonewalker", "mon_shambler", "mon_wolf", "mon_bandit",
  "mon_shriekwing", "mon_tusker", "mon_sentinel", "mon_elemental", "mon_nightfeeder",
  "mon_bullguard", "mon_hillhulk", "mon_skyclaw_guard", "mon_dragon_guard",
  "mon_luckling", "mon_reveler", "mon_maw_tendril", "mon_steel_sentinel",
]);

/** Stand-in sprites ordered by how much they should look like they are worth. */
export const GUARDS_BY_DANGER: readonly (readonly string[])[] = Object.freeze([
  ["mon_cinderling", "mon_scrapper", "mon_bonewalker"],
  ["mon_shambler", "mon_wolf", "mon_bandit", "mon_luckling"],
  ["mon_shriekwing", "mon_tusker", "mon_sentinel"],
  ["mon_elemental", "mon_nightfeeder", "mon_skyclaw_guard", "mon_steel_sentinel"],
  ["mon_bullguard", "mon_hillhulk", "mon_reveler", "mon_maw_tendril"],
  ["mon_dragon_guard"],
]);

/**
 * The creature a dedicated sprite was drawn for. A generated stack of Imps
 * still wears `mon_cinderling`; everything else reuses a sprite of the same band.
 */
export const SPRITE_BY_CREATURE: Readonly<Record<string, string>> = Object.freeze({
  "emberpit.cinderling": "mon_cinderling",
  "ironhorde.scrapper": "mon_scrapper",
  "gravewatch.bonewalker": "mon_bonewalker",
  "gravewatch.shambler": "mon_shambler",
  "ironhorde.fang_rider": "mon_wolf",
  "neutral.cutpurse": "mon_bandit",
  "neutral.luckling": "mon_luckling",
  "deepvault.shriekwing": "mon_shriekwing",
  "ironhorde.tusker": "mon_tusker",
  "skyreach.clay_sentinel": "mon_sentinel",
  "prismgate.stone_spirit": "mon_elemental",
  "gravewatch.nightfeeder": "mon_nightfeeder",
  "sunspire.skyclaw": "mon_skyclaw_guard",
  "neutral.steel_sentinel": "mon_steel_sentinel",
  "deepvault.bullguard": "mon_bullguard",
  "neutral.hillhulk": "mon_hillhulk",
  "neutral.reveler": "mon_reveler",
  "neutral.maw_tendril": "mon_maw_tendril",
  "deepvault.crimson_wyrm": "mon_dragon_guard",
});

/** A wandering army, not a siege engine and not a growth-zero placeholder. */
export function isWandererCreature(c: CreatureDef): boolean {
  if (c.growth <= 0) return false;
  for (const a of c.abilities) if (a.kind === "SIEGE_MACHINE") return false;
  return true;
}

/** Danger band 0..5 from creature tier: T1 near a capital, T6-T7 at the far edge. */
export function dangerBandForTier(tier: number): number {
  if (tier <= 1) return 0;
  if (tier === 2) return 1;
  if (tier === 3) return 2;
  if (tier === 4) return 3;
  if (tier === 5) return 4;
  return 5;
}

const wandererCache = new WeakMap<ContentRegistry, readonly (readonly CreatureDef[])[]>();

/**
 * Every combat creature, bucketed by the danger band a generated map will
 * roll it in. War machines are out. Sorted by id so a walk of the table is
 * a pure function of the registry, not of Map iteration order.
 */
export function wanderersByDangerBand(
  content: ContentRegistry,
): readonly (readonly CreatureDef[])[] {
  const hit = wandererCache.get(content);
  if (hit !== undefined) return hit;
  const bands: CreatureDef[][] = [[], [], [], [], [], []];
  const all = [...content.creatures.values()]
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const c of all) {
    if (!isWandererCreature(c)) continue;
    bands[dangerBandForTier(c.tier)]!.push(c);
  }
  const frozen = Object.freeze(bands.map((b) => Object.freeze(b.slice())));
  wandererCache.set(content, frozen);
  return frozen;
}

/** A sea-going roster: amphibious factions, plus Saltreach (swamp-native sailors). */
export function seaFaction(content: ContentRegistry, faction: CreatureDef["faction"]): boolean {
  const id = faction as string;
  if (id === "saltreach" || id === "blackwater") return true;
  return content.factions.get(faction)?.amphibious === true;
}

/**
 * Prefer the zone's theme and the faction's home ground; never exclude anyone
 * on land. On water, only sea-going rosters have weight.
 */
export function wandererWeight(
  content: ContentRegistry, c: CreatureDef, terrain: TerrainKind,
  theme: CreatureDef["faction"] | null = null,
): number {
  if (terrain === "water") return seaFaction(content, c.faction) ? 8 : 0;
  let w = 1;
  if (theme !== null && c.faction === theme) w += 6;
  const f = content.factions.get(c.faction);
  if (f === undefined) return w;
  if (f.nativeTerrain === terrain) w += 3;
  const extra = f.extraNative;
  if (extra !== undefined) {
    for (const t of extra) if (t === terrain) w += 3;
  }
  return w;
}

/** The nineteen-sprite stand-in a stack of `c` wears on the adventure map. */
export function spriteForWanderer(c: CreatureDef, band: number): string {
  const dedicated = SPRITE_BY_CREATURE[c.id as string];
  if (dedicated !== undefined) return dedicated;
  let twoHex = false;
  for (const a of c.abilities) if (a.kind === "TWO_HEX") twoHex = true;
  if (twoHex && c.tier >= 6) return "mon_dragon_guard";
  if (twoHex && c.tier >= 3) return "mon_skyclaw_guard";
  const lo = band < 0 ? 0 : band > 5 ? 5 : band;
  const visuals = GUARDS_BY_DANGER[lo] ?? GUARDS_BY_DANGER[0]!;
  return stablePick(c.id as string, visuals);
}

/** Deterministic index into `xs` from an id. Not an rng roll. */
function stablePick<T>(id: string, xs: readonly T[]): T {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h ^= id.charCodeAt(i) * (i + 1);
  return xs[(h >>> 0) % xs.length]!;
}

/** Visitables safe enough to sit in a player's own neighbourhood. */
export const HOME_BONUSES: readonly string[] = Object.freeze([
  "bld_well", "bld_tavern", "bld_windmill", "bld_watermill", "bld_herb_garden",
  "bld_rune_stone", "bld_temple", "bld_lucky_totem", "bld_rally_flag",
  "bld_stables", "bld_heron_pool", "bld_spring_vigor", "bld_magic_spring",
  "bld_lean_to", "bld_wagon_camp",
]);

/** Visitables worth crossing the map for.
 *  @deprecated Scatter uses `scatterPool(content).prizeBonuses`, which is every
 *  land bonus not in `HOME_BONUSES` and not opted out. */
export const PRIZE_BONUSES: readonly string[] = Object.freeze([
  "bld_arena", "bld_mercenary_camp", "bld_star_dial", "bld_garden_insight",
  "bld_arcane_academy", "bld_library", "bld_hedge_hut", "bld_hermit_hut",
  "bld_observatory", "bld_cartographer", "bld_warcraft_keep",
  "bld_trading_post", "bld_black_market", "bld_altar_sacrifice", "bld_war_factory",
  "bld_watchtower", "bld_sentinel_spire", "bld_elder_oak", "bld_university",
  "bld_old_altar", "bld_spell_arena", "bld_gazebo",
  "bld_hero_camp", "bld_alchemy_lab", "bld_oath_temple",
  "bld_watering_hole", "bld_trail_post", "bld_mana_altar",
  "bld_warcraft_camp", "bld_tinker_shop", "bld_scrap_trader",
  "bld_bone_press", "bld_mineral_spring", "bld_recluse_shack",
  "bld_chaos_star",
]);

/**
 * The Oracle's Hut is off `PRIZE_BONUSES` on purpose.
 *
 * Every other row of that table is a def the generator can drop anywhere and
 * forget about; a hut is only a hut once something has written a quest into its
 * payload, and an unarged hut hands its reward to the first hero through the
 * door. It is placed by `placeQuests`, which writes the quest and the reward
 * together, and never by the scatter.
 */
export const SEER_HUTS: readonly string[] = Object.freeze([
  "bld_oracle_hut",
]);

export const SHRINES: readonly string[] = Object.freeze([
  "bld_shrine", "bld_shrine_mystery",
]);

export const CHESTS: readonly string[] = Object.freeze([
  "pu_chest", "pu_treasure_hoard", "pu_map_fragment", "pu_campfire", "bld_lonely_grave",
]);

export const SEA_PICKUPS: readonly string[] = Object.freeze([
  "bld_jetsam", "bld_sea_barrel",
]);

/**
 * Visitables that only make sense out on the water.
 *
 * Kept apart from `PRIZE_BONUSES` because that table is scattered over land
 * cells and a Mermaid rock on a meadow is not a placement mistake the renderer
 * will catch - it draws perfectly well and reads as a landmark nobody can
 * explain.
 */
export const SEA_BONUSES: readonly string[] = Object.freeze([
  "bld_tide_maidens", "bld_siren_rocks", "bld_sea_lookout", "bld_sea_temple",
]);

export const BOATS: readonly string[] = Object.freeze([
  "boat_raft", "boat_sail", "boat_war_galley",
]);

/** Terrain a structure looks wrong on. Empty means "anywhere on land". */
export const TERRAIN_BAN: Readonly<Record<string, readonly TerrainKind[]>> = Object.freeze({
  mine_sawmill: ["sand", "lava", "snow", "wasteland"],
  mine_peat_bog: ["sand", "lava"],
  mine_salt_flat: ["swamp", "grass"],
  bld_windmill: ["lava", "swamp", "wasteland"],
  bld_watermill: ["sand", "lava", "wasteland"],
  bld_heron_pool: ["lava", "sand", "wasteland"],
  bld_oasis: ["snow", "swamp", "grass", "highlands"],
  bld_spring_vigor: ["lava"],
  bld_herb_garden: ["lava", "sand", "wasteland"],
  dwl_glimmer_ring: ["lava", "sand", "snow", "wasteland"],
  dwl_grove: ["lava", "sand", "snow", "wasteland"],
  dwl_treehouse: ["lava", "sand", "snow", "wasteland"],
  dwl_tempter_gate: ["snow", "grass"],
  bank_wyrm_hoard: ["swamp"],
});

/** Objects that only make sense on water. */
export const WATER_ONLY: ReadonlySet<string> = new Set([
  "boat_raft", "boat_sail", "boat_war_galley", "boat_airship",
  "tp_whirlpool", "bld_jetsam", "bld_sea_barrel", "bank_shipwreck",
  "bld_tide_maidens", "bld_siren_rocks", "bld_sea_lookout", "bld_sea_temple",
]);

// ---------------------------------------------------------------- decoration

/**
 * The mountain sprites stand ON the ridge, and the ridge is rock terrain, so
 * these are the only defs `terrainOk` allows on a rock cell. Their footprints
 * block nothing new - a rock cell is already impassable - and they carry no
 * entrance, so the reachability audit skips them the way it skips any scenery.
 * Everything else stays refused on rock: a well or a wandering monster standing
 * on a cliff face is a placement bug, not a look.
 */
export const ROCK_DWELLING: ReadonlySet<string> = new Set([
  // The range kit: each id is one 64px COLUMN cut from a generated massif
  // (game/tools/range-kit.py), so a run of consecutive columns reassembles
  // into one seamless mountain range, plus the two knolls that stand alone.
  // One-tile defs on purpose: the 2x1 cones they replace could only be placed
  // where two horizontally adjacent rock cells existed, which a one-cell-wide
  // diagonal ridge almost never has, so most of the wall stayed bare gravel.
  //
  // The rounded kit (2026-09-02): interior columns `X0..`, each massif's
  // western and eastern END CAP `X_L` / `X_R`, the short two-summit massifs,
  // and the solo peaks a one-cell ridge wears. `obstacleWalls.ts` still counts
  // the columns out of the registry; this set only ALLOWS an id on rock, and
  // `mapgen.spec.ts` checks every id here is a real def - so a re-cut that
  // changes a column count is caught there, not by a hole in a wall.
  // range-kit:begin (written by game/tools/range-kit.py install; do not edit)
  "obs_mtn_crag_a0", "obs_mtn_crag_a1", "obs_mtn_crag_a2", "obs_mtn_crag_a3",
  "obs_mtn_crag_a_L", "obs_mtn_crag_a_R", "obs_mtn_crag_a_cL0", "obs_mtn_crag_a_cL1",
  "obs_mtn_crag_a_cL2", "obs_mtn_crag_a_cL3", "obs_mtn_crag_a_cR0", "obs_mtn_crag_a_cR1",
  "obs_mtn_crag_a_cR2", "obs_mtn_crag_a_cR3", "obs_mtn_crag_a_cS0", "obs_mtn_crag_a_cS1",
  "obs_mtn_crag_a_cS3", "obs_mtn_crag_a_m0", "obs_mtn_crag_a_m1", "obs_mtn_crag_a_m2",
  "obs_mtn_crag_a_m3", "obs_mtn_crag_a_m_L", "obs_mtn_crag_a_m_R", "obs_mtn_crag_a_m_cL0",
  "obs_mtn_crag_a_m_cL1", "obs_mtn_crag_a_m_cL2", "obs_mtn_crag_a_m_cL3",
  "obs_mtn_crag_a_m_cR0", "obs_mtn_crag_a_m_cR1", "obs_mtn_crag_a_m_cR2",
  "obs_mtn_crag_a_m_cR3", "obs_mtn_crag_a_m_cS0", "obs_mtn_crag_a_m_cS1",
  "obs_mtn_crag_a_m_cS2", "obs_mtn_crag_a_m_cS3", "obs_mtn_crag_b0", "obs_mtn_crag_b1",
  "obs_mtn_crag_b2", "obs_mtn_crag_b3", "obs_mtn_crag_b_L", "obs_mtn_crag_b_R",
  "obs_mtn_crag_b_cL0", "obs_mtn_crag_b_cL1", "obs_mtn_crag_b_cL2", "obs_mtn_crag_b_cL3",
  "obs_mtn_crag_b_cR0", "obs_mtn_crag_b_cR1", "obs_mtn_crag_b_cR2", "obs_mtn_crag_b_cR3",
  "obs_mtn_crag_b_cS1", "obs_mtn_crag_b_cS2", "obs_mtn_crag_b_cS3", "obs_mtn_crag_b_m0",
  "obs_mtn_crag_b_m1", "obs_mtn_crag_b_m2", "obs_mtn_crag_b_m3", "obs_mtn_crag_b_m_L",
  "obs_mtn_crag_b_m_R", "obs_mtn_crag_b_m_cL0", "obs_mtn_crag_b_m_cL1", "obs_mtn_crag_b_m_cL2",
  "obs_mtn_crag_b_m_cL3", "obs_mtn_crag_b_m_cR0", "obs_mtn_crag_b_m_cR1",
  "obs_mtn_crag_b_m_cR2", "obs_mtn_crag_b_m_cR3", "obs_mtn_crag_b_m_cS1",
  "obs_mtn_crag_b_m_cS2", "obs_mtn_crag_c0", "obs_mtn_crag_c1", "obs_mtn_crag_c2",
  "obs_mtn_crag_c3", "obs_mtn_crag_c4", "obs_mtn_crag_c5", "obs_mtn_crag_c6",
  "obs_mtn_crag_c7", "obs_mtn_crag_c8", "obs_mtn_crag_c9", "obs_mtn_crag_c_L",
  "obs_mtn_crag_c_R", "obs_mtn_crag_c_cL0", "obs_mtn_crag_c_cL1", "obs_mtn_crag_c_cL2",
  "obs_mtn_crag_c_cL3", "obs_mtn_crag_c_cL4", "obs_mtn_crag_c_cL5", "obs_mtn_crag_c_cL6",
  "obs_mtn_crag_c_cL7", "obs_mtn_crag_c_cL8", "obs_mtn_crag_c_cL9", "obs_mtn_crag_c_cR0",
  "obs_mtn_crag_c_cR1", "obs_mtn_crag_c_cR2", "obs_mtn_crag_c_cR3", "obs_mtn_crag_c_cR4",
  "obs_mtn_crag_c_cR5", "obs_mtn_crag_c_cR6", "obs_mtn_crag_c_cR7", "obs_mtn_crag_c_cR8",
  "obs_mtn_crag_c_cR9", "obs_mtn_crag_c_m0", "obs_mtn_crag_c_m1", "obs_mtn_crag_c_m2",
  "obs_mtn_crag_c_m3", "obs_mtn_crag_c_m4", "obs_mtn_crag_c_m5", "obs_mtn_crag_c_m6",
  "obs_mtn_crag_c_m7", "obs_mtn_crag_c_m8", "obs_mtn_crag_c_m9", "obs_mtn_crag_c_m_L",
  "obs_mtn_crag_c_m_R", "obs_mtn_crag_c_m_cL0", "obs_mtn_crag_c_m_cL1", "obs_mtn_crag_c_m_cL2",
  "obs_mtn_crag_c_m_cL3", "obs_mtn_crag_c_m_cL4", "obs_mtn_crag_c_m_cL5",
  "obs_mtn_crag_c_m_cL6", "obs_mtn_crag_c_m_cL7", "obs_mtn_crag_c_m_cL8",
  "obs_mtn_crag_c_m_cL9", "obs_mtn_crag_c_m_cR0", "obs_mtn_crag_c_m_cR1",
  "obs_mtn_crag_c_m_cR2", "obs_mtn_crag_c_m_cR3", "obs_mtn_crag_c_m_cR4",
  "obs_mtn_crag_c_m_cR5", "obs_mtn_crag_c_m_cR6", "obs_mtn_crag_c_m_cR7",
  "obs_mtn_crag_c_m_cR8", "obs_mtn_crag_c_m_cR9", "obs_mtn_foot_a", "obs_mtn_foot_a_low",
  "obs_mtn_foot_a_ridge_a", "obs_mtn_foot_a_ridge_b", "obs_mtn_foot_a_ridge_c",
  "obs_mtn_foot_a_ridge_d", "obs_mtn_foot_a_ridge_e", "obs_mtn_foot_a_ridge_f",
  "obs_mtn_foot_a_ridge_g", "obs_mtn_foot_a_ridge_h", "obs_mtn_foot_a_scatter",
  "obs_mtn_foot_lava", "obs_mtn_foot_lava_low", "obs_mtn_foot_lava_ridge_a",
  "obs_mtn_foot_lava_ridge_b", "obs_mtn_foot_lava_ridge_c", "obs_mtn_foot_lava_ridge_d",
  "obs_mtn_foot_lava_ridge_e", "obs_mtn_foot_lava_ridge_f", "obs_mtn_foot_lava_ridge_g",
  "obs_mtn_foot_lava_ridge_h", "obs_mtn_foot_lava_scatter", "obs_mtn_foot_snow",
  "obs_mtn_foot_snow_low", "obs_mtn_foot_snow_ridge_a", "obs_mtn_foot_snow_ridge_b",
  "obs_mtn_foot_snow_ridge_c", "obs_mtn_foot_snow_ridge_d", "obs_mtn_foot_snow_ridge_e",
  "obs_mtn_foot_snow_ridge_f", "obs_mtn_foot_snow_ridge_g", "obs_mtn_foot_snow_ridge_h",
  "obs_mtn_foot_snow_scatter", "obs_mtn_foot_swamp", "obs_mtn_foot_swamp_low",
  "obs_mtn_foot_swamp_ridge_a", "obs_mtn_foot_swamp_ridge_b", "obs_mtn_foot_swamp_ridge_c",
  "obs_mtn_foot_swamp_scatter", "obs_mtn_knoll", "obs_mtn_knoll_snow", "obs_mtn_lava_a0",
  "obs_mtn_lava_a1", "obs_mtn_lava_a2", "obs_mtn_lava_a_L", "obs_mtn_lava_a_R",
  "obs_mtn_lava_a_cL0", "obs_mtn_lava_a_cL1", "obs_mtn_lava_a_cL2", "obs_mtn_lava_a_cR0",
  "obs_mtn_lava_a_cR1", "obs_mtn_lava_a_cR2", "obs_mtn_lava_a_cS0", "obs_mtn_lava_a_cS1",
  "obs_mtn_lava_a_cS2", "obs_mtn_lava_a_m0", "obs_mtn_lava_a_m1", "obs_mtn_lava_a_m2",
  "obs_mtn_lava_a_m_L", "obs_mtn_lava_a_m_R", "obs_mtn_lava_a_m_cL0", "obs_mtn_lava_a_m_cL1",
  "obs_mtn_lava_a_m_cL2", "obs_mtn_lava_a_m_cR0", "obs_mtn_lava_a_m_cR1",
  "obs_mtn_lava_a_m_cR2", "obs_mtn_lava_a_m_cS0", "obs_mtn_lava_a_m_cS1",
  "obs_mtn_lava_a_m_cS2", "obs_mtn_lava_b0", "obs_mtn_lava_b1", "obs_mtn_lava_b2",
  "obs_mtn_lava_b3", "obs_mtn_lava_b4", "obs_mtn_lava_b5", "obs_mtn_lava_b6",
  "obs_mtn_lava_b_L", "obs_mtn_lava_b_R", "obs_mtn_lava_b_cL0", "obs_mtn_lava_b_cL1",
  "obs_mtn_lava_b_cL2", "obs_mtn_lava_b_cL3", "obs_mtn_lava_b_cL4", "obs_mtn_lava_b_cL5",
  "obs_mtn_lava_b_cL6", "obs_mtn_lava_b_cR0", "obs_mtn_lava_b_cR1", "obs_mtn_lava_b_cR2",
  "obs_mtn_lava_b_cR3", "obs_mtn_lava_b_cR4", "obs_mtn_lava_b_cR5", "obs_mtn_lava_b_cR6",
  "obs_mtn_lava_b_cS0", "obs_mtn_lava_b_cS1", "obs_mtn_lava_b_cS2", "obs_mtn_lava_b_cS3",
  "obs_mtn_lava_b_cS4", "obs_mtn_lava_b_cS5", "obs_mtn_lava_b_cS6", "obs_mtn_lava_b_m0",
  "obs_mtn_lava_b_m1", "obs_mtn_lava_b_m2", "obs_mtn_lava_b_m3", "obs_mtn_lava_b_m4",
  "obs_mtn_lava_b_m5", "obs_mtn_lava_b_m6", "obs_mtn_lava_b_m_L", "obs_mtn_lava_b_m_R",
  "obs_mtn_lava_b_m_cL0", "obs_mtn_lava_b_m_cL1", "obs_mtn_lava_b_m_cL2",
  "obs_mtn_lava_b_m_cL3", "obs_mtn_lava_b_m_cL4", "obs_mtn_lava_b_m_cL5",
  "obs_mtn_lava_b_m_cL6", "obs_mtn_lava_b_m_cR0", "obs_mtn_lava_b_m_cR1",
  "obs_mtn_lava_b_m_cR2", "obs_mtn_lava_b_m_cR3", "obs_mtn_lava_b_m_cR4",
  "obs_mtn_lava_b_m_cR5", "obs_mtn_lava_b_m_cR6", "obs_mtn_lava_b_m_cS0",
  "obs_mtn_lava_b_m_cS1", "obs_mtn_lava_b_m_cS2", "obs_mtn_lava_b_m_cS3",
  "obs_mtn_lava_b_m_cS4", "obs_mtn_lava_b_m_cS5", "obs_mtn_lava_b_m_cS6",
  "obs_mtn_lava_crag_a0", "obs_mtn_lava_crag_a1", "obs_mtn_lava_crag_a2",
  "obs_mtn_lava_crag_a3", "obs_mtn_lava_crag_a_L", "obs_mtn_lava_crag_a_R",
  "obs_mtn_lava_crag_a_cL0", "obs_mtn_lava_crag_a_cL1", "obs_mtn_lava_crag_a_cL2",
  "obs_mtn_lava_crag_a_cL3", "obs_mtn_lava_crag_a_cR0", "obs_mtn_lava_crag_a_cR1",
  "obs_mtn_lava_crag_a_cR2", "obs_mtn_lava_crag_a_cR3", "obs_mtn_lava_crag_a_cS0",
  "obs_mtn_lava_crag_a_cS1", "obs_mtn_lava_crag_a_cS2", "obs_mtn_lava_crag_a_m0",
  "obs_mtn_lava_crag_a_m1", "obs_mtn_lava_crag_a_m2", "obs_mtn_lava_crag_a_m3",
  "obs_mtn_lava_crag_a_m_L", "obs_mtn_lava_crag_a_m_R", "obs_mtn_lava_crag_a_m_cL0",
  "obs_mtn_lava_crag_a_m_cL1", "obs_mtn_lava_crag_a_m_cL2", "obs_mtn_lava_crag_a_m_cL3",
  "obs_mtn_lava_crag_a_m_cR0", "obs_mtn_lava_crag_a_m_cR1", "obs_mtn_lava_crag_a_m_cR2",
  "obs_mtn_lava_crag_a_m_cR3", "obs_mtn_lava_crag_a_m_cS0", "obs_mtn_lava_crag_a_m_cS1",
  "obs_mtn_lava_crag_a_m_cS2", "obs_mtn_lava_crag_a_m_cS3", "obs_mtn_lava_crag_b0",
  "obs_mtn_lava_crag_b1", "obs_mtn_lava_crag_b2", "obs_mtn_lava_crag_b3",
  "obs_mtn_lava_crag_b_L", "obs_mtn_lava_crag_b_R", "obs_mtn_lava_crag_b_cL0",
  "obs_mtn_lava_crag_b_cL1", "obs_mtn_lava_crag_b_cL2", "obs_mtn_lava_crag_b_cL3",
  "obs_mtn_lava_crag_b_cR0", "obs_mtn_lava_crag_b_cR1", "obs_mtn_lava_crag_b_cR2",
  "obs_mtn_lava_crag_b_cR3", "obs_mtn_lava_crag_b_cS1", "obs_mtn_lava_crag_b_cS2",
  "obs_mtn_lava_crag_b_cS3", "obs_mtn_lava_crag_b_m0", "obs_mtn_lava_crag_b_m1",
  "obs_mtn_lava_crag_b_m2", "obs_mtn_lava_crag_b_m3", "obs_mtn_lava_crag_b_m_L",
  "obs_mtn_lava_crag_b_m_R", "obs_mtn_lava_crag_b_m_cL0", "obs_mtn_lava_crag_b_m_cL1",
  "obs_mtn_lava_crag_b_m_cL2", "obs_mtn_lava_crag_b_m_cL3", "obs_mtn_lava_crag_b_m_cR0",
  "obs_mtn_lava_crag_b_m_cR1", "obs_mtn_lava_crag_b_m_cR2", "obs_mtn_lava_crag_b_m_cR3",
  "obs_mtn_lava_crag_b_m_cS0", "obs_mtn_lava_crag_b_m_cS1", "obs_mtn_lava_crag_b_m_cS2",
  "obs_mtn_lava_crag_c0", "obs_mtn_lava_crag_c1", "obs_mtn_lava_crag_c2",
  "obs_mtn_lava_crag_c3", "obs_mtn_lava_crag_c4", "obs_mtn_lava_crag_c5",
  "obs_mtn_lava_crag_c6", "obs_mtn_lava_crag_c7", "obs_mtn_lava_crag_c8",
  "obs_mtn_lava_crag_c9", "obs_mtn_lava_crag_c_L", "obs_mtn_lava_crag_c_R",
  "obs_mtn_lava_crag_c_cL0", "obs_mtn_lava_crag_c_cL1", "obs_mtn_lava_crag_c_cL2",
  "obs_mtn_lava_crag_c_cL3", "obs_mtn_lava_crag_c_cL4", "obs_mtn_lava_crag_c_cL5",
  "obs_mtn_lava_crag_c_cL6", "obs_mtn_lava_crag_c_cL7", "obs_mtn_lava_crag_c_cL8",
  "obs_mtn_lava_crag_c_cL9", "obs_mtn_lava_crag_c_cR0", "obs_mtn_lava_crag_c_cR1",
  "obs_mtn_lava_crag_c_cR2", "obs_mtn_lava_crag_c_cR3", "obs_mtn_lava_crag_c_cR4",
  "obs_mtn_lava_crag_c_cR5", "obs_mtn_lava_crag_c_cR6", "obs_mtn_lava_crag_c_cR7",
  "obs_mtn_lava_crag_c_cR8", "obs_mtn_lava_crag_c_cR9", "obs_mtn_lava_crag_c_cS1",
  "obs_mtn_lava_crag_c_cS2", "obs_mtn_lava_crag_c_cS3", "obs_mtn_lava_crag_c_cS4",
  "obs_mtn_lava_crag_c_cS5", "obs_mtn_lava_crag_c_cS6", "obs_mtn_lava_crag_c_cS7",
  "obs_mtn_lava_crag_c_m0", "obs_mtn_lava_crag_c_m1", "obs_mtn_lava_crag_c_m2",
  "obs_mtn_lava_crag_c_m3", "obs_mtn_lava_crag_c_m4", "obs_mtn_lava_crag_c_m5",
  "obs_mtn_lava_crag_c_m6", "obs_mtn_lava_crag_c_m7", "obs_mtn_lava_crag_c_m8",
  "obs_mtn_lava_crag_c_m9", "obs_mtn_lava_crag_c_m_L", "obs_mtn_lava_crag_c_m_R",
  "obs_mtn_lava_crag_c_m_cL0", "obs_mtn_lava_crag_c_m_cL1", "obs_mtn_lava_crag_c_m_cL2",
  "obs_mtn_lava_crag_c_m_cL3", "obs_mtn_lava_crag_c_m_cL4", "obs_mtn_lava_crag_c_m_cL5",
  "obs_mtn_lava_crag_c_m_cL6", "obs_mtn_lava_crag_c_m_cL7", "obs_mtn_lava_crag_c_m_cL8",
  "obs_mtn_lava_crag_c_m_cL9", "obs_mtn_lava_crag_c_m_cR0", "obs_mtn_lava_crag_c_m_cR1",
  "obs_mtn_lava_crag_c_m_cR2", "obs_mtn_lava_crag_c_m_cR3", "obs_mtn_lava_crag_c_m_cR4",
  "obs_mtn_lava_crag_c_m_cR5", "obs_mtn_lava_crag_c_m_cR6", "obs_mtn_lava_crag_c_m_cR7",
  "obs_mtn_lava_crag_c_m_cR8", "obs_mtn_lava_crag_c_m_cR9", "obs_mtn_lava_crag_c_m_cS2",
  "obs_mtn_lava_crag_c_m_cS3", "obs_mtn_lava_crag_c_m_cS4", "obs_mtn_lava_crag_c_m_cS5",
  "obs_mtn_lava_crag_c_m_cS6", "obs_mtn_lava_crag_c_m_cS7", "obs_mtn_lava_crag_c_m_cS8",
  "obs_mtn_lava_mass_a0", "obs_mtn_lava_mass_a1", "obs_mtn_lava_mass_a_L",
  "obs_mtn_lava_mass_a_R", "obs_mtn_lava_mass_a_cL0", "obs_mtn_lava_mass_a_cL1",
  "obs_mtn_lava_mass_a_cR0", "obs_mtn_lava_mass_a_cR1", "obs_mtn_lava_mass_a_cS0",
  "obs_mtn_lava_mass_a_cS1", "obs_mtn_lava_mass_a_m0", "obs_mtn_lava_mass_a_m1",
  "obs_mtn_lava_mass_a_m_L", "obs_mtn_lava_mass_a_m_R", "obs_mtn_lava_mass_a_m_cL0",
  "obs_mtn_lava_mass_a_m_cL1", "obs_mtn_lava_mass_a_m_cR0", "obs_mtn_lava_mass_a_m_cR1",
  "obs_mtn_lava_mass_a_m_cS0", "obs_mtn_lava_mass_a_m_cS1", "obs_mtn_lava_mass_b0",
  "obs_mtn_lava_mass_b1", "obs_mtn_lava_mass_b2", "obs_mtn_lava_mass_b3",
  "obs_mtn_lava_mass_b_L", "obs_mtn_lava_mass_b_R", "obs_mtn_lava_mass_b_cL0",
  "obs_mtn_lava_mass_b_cL1", "obs_mtn_lava_mass_b_cL2", "obs_mtn_lava_mass_b_cL3",
  "obs_mtn_lava_mass_b_cR0", "obs_mtn_lava_mass_b_cR1", "obs_mtn_lava_mass_b_cR2",
  "obs_mtn_lava_mass_b_cR3", "obs_mtn_lava_mass_b_cS0", "obs_mtn_lava_mass_b_cS1",
  "obs_mtn_lava_mass_b_cS2", "obs_mtn_lava_mass_b_cS3", "obs_mtn_lava_mass_b_m0",
  "obs_mtn_lava_mass_b_m1", "obs_mtn_lava_mass_b_m2", "obs_mtn_lava_mass_b_m3",
  "obs_mtn_lava_mass_b_m_L", "obs_mtn_lava_mass_b_m_R", "obs_mtn_lava_mass_b_m_cL0",
  "obs_mtn_lava_mass_b_m_cL1", "obs_mtn_lava_mass_b_m_cL2", "obs_mtn_lava_mass_b_m_cL3",
  "obs_mtn_lava_mass_b_m_cR0", "obs_mtn_lava_mass_b_m_cR1", "obs_mtn_lava_mass_b_m_cR2",
  "obs_mtn_lava_mass_b_m_cR3", "obs_mtn_lava_mass_b_m_cS0", "obs_mtn_lava_mass_b_m_cS1",
  "obs_mtn_lava_mass_b_m_cS2", "obs_mtn_lava_mass_b_m_cS3", "obs_mtn_lava_mass_c0",
  "obs_mtn_lava_mass_c1", "obs_mtn_lava_mass_c2", "obs_mtn_lava_mass_c_L",
  "obs_mtn_lava_mass_c_R", "obs_mtn_lava_mass_c_cL0", "obs_mtn_lava_mass_c_cL1",
  "obs_mtn_lava_mass_c_cL2", "obs_mtn_lava_mass_c_cR0", "obs_mtn_lava_mass_c_cR1",
  "obs_mtn_lava_mass_c_cR2", "obs_mtn_lava_mass_c_cS0", "obs_mtn_lava_mass_c_cS1",
  "obs_mtn_lava_mass_c_cS2", "obs_mtn_lava_mass_c_m0", "obs_mtn_lava_mass_c_m1",
  "obs_mtn_lava_mass_c_m2", "obs_mtn_lava_mass_c_m_L", "obs_mtn_lava_mass_c_m_R",
  "obs_mtn_lava_mass_c_m_cL0", "obs_mtn_lava_mass_c_m_cL1", "obs_mtn_lava_mass_c_m_cL2",
  "obs_mtn_lava_mass_c_m_cR0", "obs_mtn_lava_mass_c_m_cR1", "obs_mtn_lava_mass_c_m_cR2",
  "obs_mtn_lava_mass_c_m_cS0", "obs_mtn_lava_mass_c_m_cS1", "obs_mtn_lava_mass_c_m_cS2",
  "obs_mtn_lava_mass_d0", "obs_mtn_lava_mass_d_L", "obs_mtn_lava_mass_d_R",
  "obs_mtn_lava_mass_d_cL0", "obs_mtn_lava_mass_d_cR0", "obs_mtn_lava_mass_d_cS0",
  "obs_mtn_lava_mass_d_m0", "obs_mtn_lava_mass_d_m_L", "obs_mtn_lava_mass_d_m_R",
  "obs_mtn_lava_mass_d_m_cL0", "obs_mtn_lava_mass_d_m_cR0", "obs_mtn_lava_mass_d_m_cS0",
  "obs_mtn_lava_mass_e0", "obs_mtn_lava_mass_e1", "obs_mtn_lava_mass_e2",
  "obs_mtn_lava_mass_e3", "obs_mtn_lava_mass_e4", "obs_mtn_lava_mass_e_L",
  "obs_mtn_lava_mass_e_R", "obs_mtn_lava_mass_e_cL0", "obs_mtn_lava_mass_e_cL1",
  "obs_mtn_lava_mass_e_cL2", "obs_mtn_lava_mass_e_cL3", "obs_mtn_lava_mass_e_cL4",
  "obs_mtn_lava_mass_e_cR0", "obs_mtn_lava_mass_e_cR1", "obs_mtn_lava_mass_e_cR2",
  "obs_mtn_lava_mass_e_cR3", "obs_mtn_lava_mass_e_cR4", "obs_mtn_lava_mass_e_cS0",
  "obs_mtn_lava_mass_e_cS1", "obs_mtn_lava_mass_e_cS2", "obs_mtn_lava_mass_e_cS4",
  "obs_mtn_lava_mass_e_m0", "obs_mtn_lava_mass_e_m1", "obs_mtn_lava_mass_e_m2",
  "obs_mtn_lava_mass_e_m3", "obs_mtn_lava_mass_e_m4", "obs_mtn_lava_mass_e_m_L",
  "obs_mtn_lava_mass_e_m_R", "obs_mtn_lava_mass_e_m_cL0", "obs_mtn_lava_mass_e_m_cL1",
  "obs_mtn_lava_mass_e_m_cL2", "obs_mtn_lava_mass_e_m_cL3", "obs_mtn_lava_mass_e_m_cL4",
  "obs_mtn_lava_mass_e_m_cR0", "obs_mtn_lava_mass_e_m_cR1", "obs_mtn_lava_mass_e_m_cR2",
  "obs_mtn_lava_mass_e_m_cR3", "obs_mtn_lava_mass_e_m_cR4", "obs_mtn_lava_mass_e_m_cS0",
  "obs_mtn_lava_mass_e_m_cS1", "obs_mtn_lava_mass_e_m_cS2", "obs_mtn_lava_mass_e_m_cS3",
  "obs_mtn_lava_mass_e_m_cS4", "obs_mtn_lava_mass_f0", "obs_mtn_lava_mass_f1",
  "obs_mtn_lava_mass_f_L", "obs_mtn_lava_mass_f_R", "obs_mtn_lava_mass_f_cL0",
  "obs_mtn_lava_mass_f_cL1", "obs_mtn_lava_mass_f_cR0", "obs_mtn_lava_mass_f_cR1",
  "obs_mtn_lava_mass_f_cS0", "obs_mtn_lava_mass_f_cS1", "obs_mtn_lava_mass_f_m0",
  "obs_mtn_lava_mass_f_m1", "obs_mtn_lava_mass_f_m_L", "obs_mtn_lava_mass_f_m_R",
  "obs_mtn_lava_mass_f_m_cL0", "obs_mtn_lava_mass_f_m_cL1", "obs_mtn_lava_mass_f_m_cR0",
  "obs_mtn_lava_mass_f_m_cR1", "obs_mtn_lava_mass_f_m_cS0", "obs_mtn_lava_mass_f_m_cS1",
  "obs_mtn_lava_mass_g0", "obs_mtn_lava_mass_g1", "obs_mtn_lava_mass_g2",
  "obs_mtn_lava_mass_g3", "obs_mtn_lava_mass_g4", "obs_mtn_lava_mass_g5",
  "obs_mtn_lava_mass_g_L", "obs_mtn_lava_mass_g_R", "obs_mtn_lava_mass_g_cL0",
  "obs_mtn_lava_mass_g_cL1", "obs_mtn_lava_mass_g_cL2", "obs_mtn_lava_mass_g_cL3",
  "obs_mtn_lava_mass_g_cL4", "obs_mtn_lava_mass_g_cL5", "obs_mtn_lava_mass_g_cR0",
  "obs_mtn_lava_mass_g_cR1", "obs_mtn_lava_mass_g_cR2", "obs_mtn_lava_mass_g_cR3",
  "obs_mtn_lava_mass_g_cR4", "obs_mtn_lava_mass_g_cR5", "obs_mtn_lava_mass_g_cS0",
  "obs_mtn_lava_mass_g_cS1", "obs_mtn_lava_mass_g_cS2", "obs_mtn_lava_mass_g_cS3",
  "obs_mtn_lava_mass_g_cS4", "obs_mtn_lava_mass_g_cS5", "obs_mtn_lava_mass_g_m0",
  "obs_mtn_lava_mass_g_m1", "obs_mtn_lava_mass_g_m2", "obs_mtn_lava_mass_g_m3",
  "obs_mtn_lava_mass_g_m4", "obs_mtn_lava_mass_g_m5", "obs_mtn_lava_mass_g_m_L",
  "obs_mtn_lava_mass_g_m_R", "obs_mtn_lava_mass_g_m_cL0", "obs_mtn_lava_mass_g_m_cL1",
  "obs_mtn_lava_mass_g_m_cL2", "obs_mtn_lava_mass_g_m_cL3", "obs_mtn_lava_mass_g_m_cL4",
  "obs_mtn_lava_mass_g_m_cL5", "obs_mtn_lava_mass_g_m_cR0", "obs_mtn_lava_mass_g_m_cR1",
  "obs_mtn_lava_mass_g_m_cR2", "obs_mtn_lava_mass_g_m_cR3", "obs_mtn_lava_mass_g_m_cR4",
  "obs_mtn_lava_mass_g_m_cR5", "obs_mtn_lava_mass_g_m_cS1", "obs_mtn_lava_mass_g_m_cS2",
  "obs_mtn_lava_mass_g_m_cS3", "obs_mtn_lava_mass_g_m_cS4", "obs_mtn_lava_mass_g_m_cS5",
  "obs_mtn_lava_mass_h0", "obs_mtn_lava_mass_h1", "obs_mtn_lava_mass_h_L",
  "obs_mtn_lava_mass_h_R", "obs_mtn_lava_mass_h_cL0", "obs_mtn_lava_mass_h_cL1",
  "obs_mtn_lava_mass_h_cR0", "obs_mtn_lava_mass_h_cR1", "obs_mtn_lava_mass_h_cS0",
  "obs_mtn_lava_mass_h_cS1", "obs_mtn_lava_mass_h_m0", "obs_mtn_lava_mass_h_m1",
  "obs_mtn_lava_mass_h_m_L", "obs_mtn_lava_mass_h_m_R", "obs_mtn_lava_mass_h_m_cL0",
  "obs_mtn_lava_mass_h_m_cL1", "obs_mtn_lava_mass_h_m_cR0", "obs_mtn_lava_mass_h_m_cR1",
  "obs_mtn_lava_mass_h_m_cS0", "obs_mtn_lava_mass_h_m_cS1", "obs_mtn_lava_mass_i0",
  "obs_mtn_lava_mass_i1", "obs_mtn_lava_mass_i2", "obs_mtn_lava_mass_i3",
  "obs_mtn_lava_mass_i4", "obs_mtn_lava_mass_i5", "obs_mtn_lava_mass_i_L",
  "obs_mtn_lava_mass_i_R", "obs_mtn_lava_mass_i_cL0", "obs_mtn_lava_mass_i_cL1",
  "obs_mtn_lava_mass_i_cL2", "obs_mtn_lava_mass_i_cL3", "obs_mtn_lava_mass_i_cL4",
  "obs_mtn_lava_mass_i_cL5", "obs_mtn_lava_mass_i_cR0", "obs_mtn_lava_mass_i_cR1",
  "obs_mtn_lava_mass_i_cR2", "obs_mtn_lava_mass_i_cR3", "obs_mtn_lava_mass_i_cR4",
  "obs_mtn_lava_mass_i_cR5", "obs_mtn_lava_mass_i_cS0", "obs_mtn_lava_mass_i_cS1",
  "obs_mtn_lava_mass_i_cS2", "obs_mtn_lava_mass_i_cS3", "obs_mtn_lava_mass_i_cS4",
  "obs_mtn_lava_mass_i_m0", "obs_mtn_lava_mass_i_m1", "obs_mtn_lava_mass_i_m2",
  "obs_mtn_lava_mass_i_m3", "obs_mtn_lava_mass_i_m4", "obs_mtn_lava_mass_i_m5",
  "obs_mtn_lava_mass_i_m_L", "obs_mtn_lava_mass_i_m_R", "obs_mtn_lava_mass_i_m_cL0",
  "obs_mtn_lava_mass_i_m_cL1", "obs_mtn_lava_mass_i_m_cL2", "obs_mtn_lava_mass_i_m_cL3",
  "obs_mtn_lava_mass_i_m_cL4", "obs_mtn_lava_mass_i_m_cL5", "obs_mtn_lava_mass_i_m_cR0",
  "obs_mtn_lava_mass_i_m_cR1", "obs_mtn_lava_mass_i_m_cR2", "obs_mtn_lava_mass_i_m_cR3",
  "obs_mtn_lava_mass_i_m_cR4", "obs_mtn_lava_mass_i_m_cR5", "obs_mtn_lava_mass_i_m_cS1",
  "obs_mtn_lava_mass_i_m_cS2", "obs_mtn_lava_mass_i_m_cS3", "obs_mtn_lava_mass_i_m_cS4",
  "obs_mtn_lava_mass_i_m_cS5", "obs_mtn_lava_mass_j0", "obs_mtn_lava_mass_j1",
  "obs_mtn_lava_mass_j2", "obs_mtn_lava_mass_j_L", "obs_mtn_lava_mass_j_R",
  "obs_mtn_lava_mass_j_cL0", "obs_mtn_lava_mass_j_cL1", "obs_mtn_lava_mass_j_cL2",
  "obs_mtn_lava_mass_j_cR0", "obs_mtn_lava_mass_j_cR1", "obs_mtn_lava_mass_j_cR2",
  "obs_mtn_lava_mass_j_cS0", "obs_mtn_lava_mass_j_cS1", "obs_mtn_lava_mass_j_cS2",
  "obs_mtn_lava_mass_j_m0", "obs_mtn_lava_mass_j_m1", "obs_mtn_lava_mass_j_m2",
  "obs_mtn_lava_mass_j_m_L", "obs_mtn_lava_mass_j_m_R", "obs_mtn_lava_mass_j_m_cL0",
  "obs_mtn_lava_mass_j_m_cL1", "obs_mtn_lava_mass_j_m_cL2", "obs_mtn_lava_mass_j_m_cR0",
  "obs_mtn_lava_mass_j_m_cR1", "obs_mtn_lava_mass_j_m_cR2", "obs_mtn_lava_mass_j_m_cS0",
  "obs_mtn_lava_mass_j_m_cS1", "obs_mtn_lava_mass_j_m_cS2", "obs_mtn_mass_a0",
  "obs_mtn_mass_a1", "obs_mtn_mass_a_L", "obs_mtn_mass_a_R", "obs_mtn_mass_a_cL0",
  "obs_mtn_mass_a_cL1", "obs_mtn_mass_a_cR0", "obs_mtn_mass_a_cR1", "obs_mtn_mass_a_cS0",
  "obs_mtn_mass_a_cS1", "obs_mtn_mass_a_m0", "obs_mtn_mass_a_m1", "obs_mtn_mass_a_m_L",
  "obs_mtn_mass_a_m_R", "obs_mtn_mass_a_m_cL0", "obs_mtn_mass_a_m_cL1", "obs_mtn_mass_a_m_cR0",
  "obs_mtn_mass_a_m_cR1", "obs_mtn_mass_a_m_cS0", "obs_mtn_mass_a_m_cS1", "obs_mtn_mass_b0",
  "obs_mtn_mass_b1", "obs_mtn_mass_b2", "obs_mtn_mass_b3", "obs_mtn_mass_b_L",
  "obs_mtn_mass_b_R", "obs_mtn_mass_b_cL0", "obs_mtn_mass_b_cL1", "obs_mtn_mass_b_cL2",
  "obs_mtn_mass_b_cL3", "obs_mtn_mass_b_cR0", "obs_mtn_mass_b_cR1", "obs_mtn_mass_b_cR2",
  "obs_mtn_mass_b_cR3", "obs_mtn_mass_b_cS0", "obs_mtn_mass_b_cS1", "obs_mtn_mass_b_cS2",
  "obs_mtn_mass_b_cS3", "obs_mtn_mass_b_m0", "obs_mtn_mass_b_m1", "obs_mtn_mass_b_m2",
  "obs_mtn_mass_b_m3", "obs_mtn_mass_b_m_L", "obs_mtn_mass_b_m_R", "obs_mtn_mass_b_m_cL0",
  "obs_mtn_mass_b_m_cL1", "obs_mtn_mass_b_m_cL2", "obs_mtn_mass_b_m_cL3",
  "obs_mtn_mass_b_m_cR0", "obs_mtn_mass_b_m_cR1", "obs_mtn_mass_b_m_cR2",
  "obs_mtn_mass_b_m_cR3", "obs_mtn_mass_b_m_cS0", "obs_mtn_mass_b_m_cS1",
  "obs_mtn_mass_b_m_cS2", "obs_mtn_mass_b_m_cS3", "obs_mtn_mass_c0", "obs_mtn_mass_c1",
  "obs_mtn_mass_c2", "obs_mtn_mass_c_L", "obs_mtn_mass_c_R", "obs_mtn_mass_c_cL0",
  "obs_mtn_mass_c_cL1", "obs_mtn_mass_c_cL2", "obs_mtn_mass_c_cR0", "obs_mtn_mass_c_cR1",
  "obs_mtn_mass_c_cR2", "obs_mtn_mass_c_cS0", "obs_mtn_mass_c_cS1", "obs_mtn_mass_c_cS2",
  "obs_mtn_mass_c_m0", "obs_mtn_mass_c_m1", "obs_mtn_mass_c_m2", "obs_mtn_mass_c_m_L",
  "obs_mtn_mass_c_m_R", "obs_mtn_mass_c_m_cL0", "obs_mtn_mass_c_m_cL1", "obs_mtn_mass_c_m_cL2",
  "obs_mtn_mass_c_m_cR0", "obs_mtn_mass_c_m_cR1", "obs_mtn_mass_c_m_cR2",
  "obs_mtn_mass_c_m_cS0", "obs_mtn_mass_c_m_cS1", "obs_mtn_mass_c_m_cS2", "obs_mtn_mass_d0",
  "obs_mtn_mass_d_L", "obs_mtn_mass_d_R", "obs_mtn_mass_d_cL0", "obs_mtn_mass_d_cR0",
  "obs_mtn_mass_d_cS0", "obs_mtn_mass_d_m0", "obs_mtn_mass_d_m_L", "obs_mtn_mass_d_m_R",
  "obs_mtn_mass_d_m_cL0", "obs_mtn_mass_d_m_cR0", "obs_mtn_mass_d_m_cS0", "obs_mtn_mass_e0",
  "obs_mtn_mass_e1", "obs_mtn_mass_e2", "obs_mtn_mass_e3", "obs_mtn_mass_e4",
  "obs_mtn_mass_e_L", "obs_mtn_mass_e_R", "obs_mtn_mass_e_cL0", "obs_mtn_mass_e_cL1",
  "obs_mtn_mass_e_cL2", "obs_mtn_mass_e_cL3", "obs_mtn_mass_e_cL4", "obs_mtn_mass_e_cR0",
  "obs_mtn_mass_e_cR1", "obs_mtn_mass_e_cR2", "obs_mtn_mass_e_cR3", "obs_mtn_mass_e_cR4",
  "obs_mtn_mass_e_cS0", "obs_mtn_mass_e_cS1", "obs_mtn_mass_e_cS2", "obs_mtn_mass_e_cS3",
  "obs_mtn_mass_e_cS4", "obs_mtn_mass_e_m0", "obs_mtn_mass_e_m1", "obs_mtn_mass_e_m2",
  "obs_mtn_mass_e_m3", "obs_mtn_mass_e_m4", "obs_mtn_mass_e_m_L", "obs_mtn_mass_e_m_R",
  "obs_mtn_mass_e_m_cL0", "obs_mtn_mass_e_m_cL1", "obs_mtn_mass_e_m_cL2",
  "obs_mtn_mass_e_m_cL3", "obs_mtn_mass_e_m_cL4", "obs_mtn_mass_e_m_cR0",
  "obs_mtn_mass_e_m_cR1", "obs_mtn_mass_e_m_cR2", "obs_mtn_mass_e_m_cR3",
  "obs_mtn_mass_e_m_cR4", "obs_mtn_mass_e_m_cS0", "obs_mtn_mass_e_m_cS1",
  "obs_mtn_mass_e_m_cS2", "obs_mtn_mass_e_m_cS3", "obs_mtn_mass_e_m_cS4", "obs_mtn_mass_f0",
  "obs_mtn_mass_f1", "obs_mtn_mass_f_L", "obs_mtn_mass_f_R", "obs_mtn_mass_f_cL0",
  "obs_mtn_mass_f_cL1", "obs_mtn_mass_f_cR0", "obs_mtn_mass_f_cR1", "obs_mtn_mass_f_cS0",
  "obs_mtn_mass_f_cS1", "obs_mtn_mass_f_m0", "obs_mtn_mass_f_m1", "obs_mtn_mass_f_m_L",
  "obs_mtn_mass_f_m_R", "obs_mtn_mass_f_m_cL0", "obs_mtn_mass_f_m_cL1", "obs_mtn_mass_f_m_cR0",
  "obs_mtn_mass_f_m_cR1", "obs_mtn_mass_f_m_cS0", "obs_mtn_mass_f_m_cS1", "obs_mtn_mass_g0",
  "obs_mtn_mass_g1", "obs_mtn_mass_g2", "obs_mtn_mass_g3", "obs_mtn_mass_g4",
  "obs_mtn_mass_g5", "obs_mtn_mass_g_L", "obs_mtn_mass_g_R", "obs_mtn_mass_g_cL0",
  "obs_mtn_mass_g_cL1", "obs_mtn_mass_g_cL2", "obs_mtn_mass_g_cL3", "obs_mtn_mass_g_cL4",
  "obs_mtn_mass_g_cL5", "obs_mtn_mass_g_cR0", "obs_mtn_mass_g_cR1", "obs_mtn_mass_g_cR2",
  "obs_mtn_mass_g_cR3", "obs_mtn_mass_g_cR4", "obs_mtn_mass_g_cR5", "obs_mtn_mass_g_cS0",
  "obs_mtn_mass_g_cS1", "obs_mtn_mass_g_cS2", "obs_mtn_mass_g_cS3", "obs_mtn_mass_g_cS4",
  "obs_mtn_mass_g_m0", "obs_mtn_mass_g_m1", "obs_mtn_mass_g_m2", "obs_mtn_mass_g_m3",
  "obs_mtn_mass_g_m4", "obs_mtn_mass_g_m5", "obs_mtn_mass_g_m_L", "obs_mtn_mass_g_m_R",
  "obs_mtn_mass_g_m_cL0", "obs_mtn_mass_g_m_cL1", "obs_mtn_mass_g_m_cL2",
  "obs_mtn_mass_g_m_cL3", "obs_mtn_mass_g_m_cL4", "obs_mtn_mass_g_m_cL5",
  "obs_mtn_mass_g_m_cR0", "obs_mtn_mass_g_m_cR1", "obs_mtn_mass_g_m_cR2",
  "obs_mtn_mass_g_m_cR3", "obs_mtn_mass_g_m_cR4", "obs_mtn_mass_g_m_cR5",
  "obs_mtn_mass_g_m_cS0", "obs_mtn_mass_g_m_cS1", "obs_mtn_mass_g_m_cS2",
  "obs_mtn_mass_g_m_cS3", "obs_mtn_mass_g_m_cS4", "obs_mtn_mass_g_m_cS5", "obs_mtn_mass_h0",
  "obs_mtn_mass_h1", "obs_mtn_mass_h_L", "obs_mtn_mass_h_R", "obs_mtn_mass_h_cL0",
  "obs_mtn_mass_h_cL1", "obs_mtn_mass_h_cR0", "obs_mtn_mass_h_cR1", "obs_mtn_mass_h_cS0",
  "obs_mtn_mass_h_cS1", "obs_mtn_mass_h_m0", "obs_mtn_mass_h_m1", "obs_mtn_mass_h_m_L",
  "obs_mtn_mass_h_m_R", "obs_mtn_mass_h_m_cL0", "obs_mtn_mass_h_m_cL1", "obs_mtn_mass_h_m_cR0",
  "obs_mtn_mass_h_m_cR1", "obs_mtn_mass_h_m_cS0", "obs_mtn_mass_h_m_cS1", "obs_mtn_mass_i0",
  "obs_mtn_mass_i1", "obs_mtn_mass_i2", "obs_mtn_mass_i3", "obs_mtn_mass_i4",
  "obs_mtn_mass_i5", "obs_mtn_mass_i_L", "obs_mtn_mass_i_R", "obs_mtn_mass_i_cL0",
  "obs_mtn_mass_i_cL1", "obs_mtn_mass_i_cL2", "obs_mtn_mass_i_cL3", "obs_mtn_mass_i_cL4",
  "obs_mtn_mass_i_cL5", "obs_mtn_mass_i_cR1", "obs_mtn_mass_i_cR2", "obs_mtn_mass_i_cR3",
  "obs_mtn_mass_i_cR4", "obs_mtn_mass_i_cR5", "obs_mtn_mass_i_cS0", "obs_mtn_mass_i_cS2",
  "obs_mtn_mass_i_cS3", "obs_mtn_mass_i_cS4", "obs_mtn_mass_i_m0", "obs_mtn_mass_i_m1",
  "obs_mtn_mass_i_m2", "obs_mtn_mass_i_m3", "obs_mtn_mass_i_m4", "obs_mtn_mass_i_m5",
  "obs_mtn_mass_i_m_L", "obs_mtn_mass_i_m_R", "obs_mtn_mass_i_m_cL0", "obs_mtn_mass_i_m_cL1",
  "obs_mtn_mass_i_m_cL2", "obs_mtn_mass_i_m_cL3", "obs_mtn_mass_i_m_cL4",
  "obs_mtn_mass_i_m_cL5", "obs_mtn_mass_i_m_cR0", "obs_mtn_mass_i_m_cR1",
  "obs_mtn_mass_i_m_cR2", "obs_mtn_mass_i_m_cR3", "obs_mtn_mass_i_m_cR4",
  "obs_mtn_mass_i_m_cR5", "obs_mtn_mass_i_m_cS0", "obs_mtn_mass_i_m_cS1",
  "obs_mtn_mass_i_m_cS2", "obs_mtn_mass_i_m_cS3", "obs_mtn_mass_i_m_cS4",
  "obs_mtn_mass_i_m_cS5", "obs_mtn_mass_j0", "obs_mtn_mass_j1", "obs_mtn_mass_j2",
  "obs_mtn_mass_j_L", "obs_mtn_mass_j_R", "obs_mtn_mass_j_cL0", "obs_mtn_mass_j_cL1",
  "obs_mtn_mass_j_cL2", "obs_mtn_mass_j_cR0", "obs_mtn_mass_j_cR1", "obs_mtn_mass_j_cR2",
  "obs_mtn_mass_j_cS0", "obs_mtn_mass_j_cS1", "obs_mtn_mass_j_cS2", "obs_mtn_mass_j_m0",
  "obs_mtn_mass_j_m1", "obs_mtn_mass_j_m2", "obs_mtn_mass_j_m_L", "obs_mtn_mass_j_m_R",
  "obs_mtn_mass_j_m_cL0", "obs_mtn_mass_j_m_cL1", "obs_mtn_mass_j_m_cL2",
  "obs_mtn_mass_j_m_cR0", "obs_mtn_mass_j_m_cR1", "obs_mtn_mass_j_m_cR2",
  "obs_mtn_mass_j_m_cS0", "obs_mtn_mass_j_m_cS1", "obs_mtn_mass_j_m_cS2",
  "obs_mtn_mass_swamp_a0", "obs_mtn_mass_swamp_a1", "obs_mtn_mass_swamp_a2",
  "obs_mtn_mass_swamp_a3", "obs_mtn_mass_swamp_a4", "obs_mtn_mass_swamp_a_L",
  "obs_mtn_mass_swamp_a_R", "obs_mtn_mass_swamp_a_cL0", "obs_mtn_mass_swamp_a_cL1",
  "obs_mtn_mass_swamp_a_cL2", "obs_mtn_mass_swamp_a_cL3", "obs_mtn_mass_swamp_a_cL4",
  "obs_mtn_mass_swamp_a_cR0", "obs_mtn_mass_swamp_a_cR1", "obs_mtn_mass_swamp_a_cR2",
  "obs_mtn_mass_swamp_a_cR3", "obs_mtn_mass_swamp_a_cR4", "obs_mtn_mass_swamp_a_cS0",
  "obs_mtn_mass_swamp_a_cS1", "obs_mtn_mass_swamp_a_cS2", "obs_mtn_mass_swamp_a_cS3",
  "obs_mtn_mass_swamp_a_cS4", "obs_mtn_mass_swamp_a_m0", "obs_mtn_mass_swamp_a_m1",
  "obs_mtn_mass_swamp_a_m2", "obs_mtn_mass_swamp_a_m3", "obs_mtn_mass_swamp_a_m4",
  "obs_mtn_mass_swamp_a_m_L", "obs_mtn_mass_swamp_a_m_R", "obs_mtn_mass_swamp_a_m_cL0",
  "obs_mtn_mass_swamp_a_m_cL1", "obs_mtn_mass_swamp_a_m_cL2", "obs_mtn_mass_swamp_a_m_cL3",
  "obs_mtn_mass_swamp_a_m_cL4", "obs_mtn_mass_swamp_a_m_cR0", "obs_mtn_mass_swamp_a_m_cR1",
  "obs_mtn_mass_swamp_a_m_cR2", "obs_mtn_mass_swamp_a_m_cR3", "obs_mtn_mass_swamp_a_m_cR4",
  "obs_mtn_mass_swamp_a_m_cS0", "obs_mtn_mass_swamp_a_m_cS1", "obs_mtn_mass_swamp_a_m_cS2",
  "obs_mtn_mass_swamp_a_m_cS3", "obs_mtn_mass_swamp_a_m_cS4", "obs_mtn_mass_swamp_b0",
  "obs_mtn_mass_swamp_b1", "obs_mtn_mass_swamp_b2", "obs_mtn_mass_swamp_b_L",
  "obs_mtn_mass_swamp_b_R", "obs_mtn_mass_swamp_b_cL0", "obs_mtn_mass_swamp_b_cL1",
  "obs_mtn_mass_swamp_b_cL2", "obs_mtn_mass_swamp_b_cR0", "obs_mtn_mass_swamp_b_cR1",
  "obs_mtn_mass_swamp_b_cR2", "obs_mtn_mass_swamp_b_cS0", "obs_mtn_mass_swamp_b_cS1",
  "obs_mtn_mass_swamp_b_cS2", "obs_mtn_mass_swamp_b_m0", "obs_mtn_mass_swamp_b_m1",
  "obs_mtn_mass_swamp_b_m2", "obs_mtn_mass_swamp_b_m_L", "obs_mtn_mass_swamp_b_m_R",
  "obs_mtn_mass_swamp_b_m_cL0", "obs_mtn_mass_swamp_b_m_cL1", "obs_mtn_mass_swamp_b_m_cL2",
  "obs_mtn_mass_swamp_b_m_cR0", "obs_mtn_mass_swamp_b_m_cR1", "obs_mtn_mass_swamp_b_m_cR2",
  "obs_mtn_mass_swamp_b_m_cS0", "obs_mtn_mass_swamp_b_m_cS1", "obs_mtn_mass_swamp_b_m_cS2",
  "obs_mtn_pair_a0", "obs_mtn_pair_a_L", "obs_mtn_pair_a_R", "obs_mtn_pair_b0",
  "obs_mtn_pair_b_L", "obs_mtn_pair_b_R", "obs_mtn_pair_crag0", "obs_mtn_pair_crag_L",
  "obs_mtn_pair_crag_R", "obs_mtn_pair_lava0", "obs_mtn_pair_lava_L", "obs_mtn_pair_lava_R",
  "obs_mtn_pair_lava_crag0", "obs_mtn_pair_lava_crag_L", "obs_mtn_pair_lava_crag_R",
  "obs_mtn_pair_snow0", "obs_mtn_pair_snow_L", "obs_mtn_pair_snow_R", "obs_mtn_pair_snow_b0",
  "obs_mtn_pair_snow_b_L", "obs_mtn_pair_snow_b_R", "obs_mtn_pair_snow_c0",
  "obs_mtn_pair_snow_c_L", "obs_mtn_pair_snow_c_R", "obs_mtn_pair_snow_crag0",
  "obs_mtn_pair_snow_crag_L", "obs_mtn_pair_snow_crag_R", "obs_mtn_pair_swamp0",
  "obs_mtn_pair_swamp_L", "obs_mtn_pair_swamp_R", "obs_mtn_peak_a", "obs_mtn_peak_b",
  "obs_mtn_peak_c", "obs_mtn_peak_crag", "obs_mtn_peak_d", "obs_mtn_peak_lava",
  "obs_mtn_peak_lava_crag", "obs_mtn_peak_lava_d", "obs_mtn_peak_snow_a",
  "obs_mtn_peak_snow_b", "obs_mtn_peak_snow_crag", "obs_mtn_peak_snow_d", "obs_mtn_peak_swamp",
  "obs_mtn_range_a0", "obs_mtn_range_a1", "obs_mtn_range_a2", "obs_mtn_range_a_L",
  "obs_mtn_range_a_R", "obs_mtn_range_a_cL0", "obs_mtn_range_a_cL1", "obs_mtn_range_a_cL2",
  "obs_mtn_range_a_cR0", "obs_mtn_range_a_cR1", "obs_mtn_range_a_cR2", "obs_mtn_range_a_cS0",
  "obs_mtn_range_a_cS1", "obs_mtn_range_a_cS2", "obs_mtn_range_a_m0", "obs_mtn_range_a_m1",
  "obs_mtn_range_a_m2", "obs_mtn_range_a_m_L", "obs_mtn_range_a_m_R", "obs_mtn_range_a_m_cL0",
  "obs_mtn_range_a_m_cL1", "obs_mtn_range_a_m_cL2", "obs_mtn_range_a_m_cR0",
  "obs_mtn_range_a_m_cR1", "obs_mtn_range_a_m_cR2", "obs_mtn_range_a_m_cS0",
  "obs_mtn_range_a_m_cS1", "obs_mtn_range_a_m_cS2", "obs_mtn_range_b0", "obs_mtn_range_b1",
  "obs_mtn_range_b2", "obs_mtn_range_b3", "obs_mtn_range_b4", "obs_mtn_range_b_L",
  "obs_mtn_range_b_R", "obs_mtn_range_b_cL0", "obs_mtn_range_b_cL1", "obs_mtn_range_b_cL2",
  "obs_mtn_range_b_cL3", "obs_mtn_range_b_cL4", "obs_mtn_range_b_cR0", "obs_mtn_range_b_cR1",
  "obs_mtn_range_b_cR2", "obs_mtn_range_b_cR3", "obs_mtn_range_b_cR4", "obs_mtn_range_b_cS0",
  "obs_mtn_range_b_cS1", "obs_mtn_range_b_cS2", "obs_mtn_range_b_cS3", "obs_mtn_range_b_cS4",
  "obs_mtn_range_b_m0", "obs_mtn_range_b_m1", "obs_mtn_range_b_m2", "obs_mtn_range_b_m3",
  "obs_mtn_range_b_m4", "obs_mtn_range_b_m_L", "obs_mtn_range_b_m_R", "obs_mtn_range_b_m_cL0",
  "obs_mtn_range_b_m_cL1", "obs_mtn_range_b_m_cL2", "obs_mtn_range_b_m_cL3",
  "obs_mtn_range_b_m_cL4", "obs_mtn_range_b_m_cR0", "obs_mtn_range_b_m_cR1",
  "obs_mtn_range_b_m_cR2", "obs_mtn_range_b_m_cR3", "obs_mtn_range_b_m_cR4",
  "obs_mtn_range_b_m_cS0", "obs_mtn_range_b_m_cS1", "obs_mtn_range_b_m_cS2",
  "obs_mtn_range_b_m_cS3", "obs_mtn_range_b_m_cS4", "obs_mtn_range_c0", "obs_mtn_range_c1",
  "obs_mtn_range_c2", "obs_mtn_range_c3", "obs_mtn_range_c_L", "obs_mtn_range_c_R",
  "obs_mtn_range_c_cL0", "obs_mtn_range_c_cL1", "obs_mtn_range_c_cL2", "obs_mtn_range_c_cL3",
  "obs_mtn_range_c_cR0", "obs_mtn_range_c_cR1", "obs_mtn_range_c_cR2", "obs_mtn_range_c_cR3",
  "obs_mtn_range_c_cS0", "obs_mtn_range_c_cS1", "obs_mtn_range_c_cS2", "obs_mtn_range_c_cS3",
  "obs_mtn_range_c_m0", "obs_mtn_range_c_m1", "obs_mtn_range_c_m2", "obs_mtn_range_c_m3",
  "obs_mtn_range_c_m_L", "obs_mtn_range_c_m_R", "obs_mtn_range_c_m_cL0",
  "obs_mtn_range_c_m_cL1", "obs_mtn_range_c_m_cL2", "obs_mtn_range_c_m_cL3",
  "obs_mtn_range_c_m_cR0", "obs_mtn_range_c_m_cR1", "obs_mtn_range_c_m_cR2",
  "obs_mtn_range_c_m_cR3", "obs_mtn_range_c_m_cS0", "obs_mtn_range_c_m_cS1",
  "obs_mtn_range_c_m_cS2", "obs_mtn_range_c_m_cS3", "obs_mtn_range_d0", "obs_mtn_range_d1",
  "obs_mtn_range_d2", "obs_mtn_range_d3", "obs_mtn_range_d4", "obs_mtn_range_d5",
  "obs_mtn_range_d_L", "obs_mtn_range_d_R", "obs_mtn_range_d_cL0", "obs_mtn_range_d_cL1",
  "obs_mtn_range_d_cL2", "obs_mtn_range_d_cL3", "obs_mtn_range_d_cL4", "obs_mtn_range_d_cL5",
  "obs_mtn_range_d_cR0", "obs_mtn_range_d_cR1", "obs_mtn_range_d_cR2", "obs_mtn_range_d_cR3",
  "obs_mtn_range_d_cR4", "obs_mtn_range_d_cR5", "obs_mtn_range_d_cS0", "obs_mtn_range_d_cS1",
  "obs_mtn_range_d_cS2", "obs_mtn_range_d_cS3", "obs_mtn_range_d_cS4", "obs_mtn_range_d_cS5",
  "obs_mtn_range_d_m0", "obs_mtn_range_d_m1", "obs_mtn_range_d_m2", "obs_mtn_range_d_m3",
  "obs_mtn_range_d_m4", "obs_mtn_range_d_m5", "obs_mtn_range_d_m_L", "obs_mtn_range_d_m_R",
  "obs_mtn_range_d_m_cL0", "obs_mtn_range_d_m_cL1", "obs_mtn_range_d_m_cL2",
  "obs_mtn_range_d_m_cL3", "obs_mtn_range_d_m_cL4", "obs_mtn_range_d_m_cL5",
  "obs_mtn_range_d_m_cR0", "obs_mtn_range_d_m_cR1", "obs_mtn_range_d_m_cR2",
  "obs_mtn_range_d_m_cR3", "obs_mtn_range_d_m_cR4", "obs_mtn_range_d_m_cR5",
  "obs_mtn_range_d_m_cS0", "obs_mtn_range_d_m_cS1", "obs_mtn_range_d_m_cS2",
  "obs_mtn_range_d_m_cS3", "obs_mtn_range_d_m_cS4", "obs_mtn_range_d_m_cS5",
  "obs_mtn_range_e0", "obs_mtn_range_e1", "obs_mtn_range_e2", "obs_mtn_range_e3",
  "obs_mtn_range_e4", "obs_mtn_range_e5", "obs_mtn_range_e6", "obs_mtn_range_e7",
  "obs_mtn_range_e8", "obs_mtn_range_e_L", "obs_mtn_range_e_R", "obs_mtn_range_e_cL0",
  "obs_mtn_range_e_cL1", "obs_mtn_range_e_cL2", "obs_mtn_range_e_cL3", "obs_mtn_range_e_cL4",
  "obs_mtn_range_e_cL5", "obs_mtn_range_e_cL6", "obs_mtn_range_e_cL7", "obs_mtn_range_e_cL8",
  "obs_mtn_range_e_cR0", "obs_mtn_range_e_cR1", "obs_mtn_range_e_cR2", "obs_mtn_range_e_cR3",
  "obs_mtn_range_e_cR4", "obs_mtn_range_e_cR5", "obs_mtn_range_e_cR6", "obs_mtn_range_e_cR7",
  "obs_mtn_range_e_cR8", "obs_mtn_range_e_cS0", "obs_mtn_range_e_cS1", "obs_mtn_range_e_cS2",
  "obs_mtn_range_e_cS3", "obs_mtn_range_e_cS4", "obs_mtn_range_e_cS5", "obs_mtn_range_e_cS6",
  "obs_mtn_range_e_cS7", "obs_mtn_range_e_cS8", "obs_mtn_range_e_m0", "obs_mtn_range_e_m1",
  "obs_mtn_range_e_m2", "obs_mtn_range_e_m3", "obs_mtn_range_e_m4", "obs_mtn_range_e_m5",
  "obs_mtn_range_e_m6", "obs_mtn_range_e_m7", "obs_mtn_range_e_m8", "obs_mtn_range_e_m_L",
  "obs_mtn_range_e_m_R", "obs_mtn_range_e_m_cL0", "obs_mtn_range_e_m_cL1",
  "obs_mtn_range_e_m_cL2", "obs_mtn_range_e_m_cL3", "obs_mtn_range_e_m_cL4",
  "obs_mtn_range_e_m_cL5", "obs_mtn_range_e_m_cL6", "obs_mtn_range_e_m_cL7",
  "obs_mtn_range_e_m_cL8", "obs_mtn_range_e_m_cR0", "obs_mtn_range_e_m_cR1",
  "obs_mtn_range_e_m_cR2", "obs_mtn_range_e_m_cR3", "obs_mtn_range_e_m_cR4",
  "obs_mtn_range_e_m_cR5", "obs_mtn_range_e_m_cR6", "obs_mtn_range_e_m_cR7",
  "obs_mtn_range_e_m_cR8", "obs_mtn_range_e_m_cS0", "obs_mtn_range_e_m_cS1",
  "obs_mtn_range_e_m_cS2", "obs_mtn_range_e_m_cS3", "obs_mtn_range_e_m_cS4",
  "obs_mtn_range_e_m_cS5", "obs_mtn_range_e_m_cS6", "obs_mtn_range_e_m_cS7",
  "obs_mtn_range_e_m_cS8", "obs_mtn_snow_a0", "obs_mtn_snow_a1", "obs_mtn_snow_a2",
  "obs_mtn_snow_a3", "obs_mtn_snow_a_L", "obs_mtn_snow_a_R", "obs_mtn_snow_a_cL0",
  "obs_mtn_snow_a_cL1", "obs_mtn_snow_a_cL2", "obs_mtn_snow_a_cL3", "obs_mtn_snow_a_cR0",
  "obs_mtn_snow_a_cR1", "obs_mtn_snow_a_cR2", "obs_mtn_snow_a_cR3", "obs_mtn_snow_a_cS0",
  "obs_mtn_snow_a_cS1", "obs_mtn_snow_a_cS2", "obs_mtn_snow_a_cS3", "obs_mtn_snow_a_m0",
  "obs_mtn_snow_a_m1", "obs_mtn_snow_a_m2", "obs_mtn_snow_a_m3", "obs_mtn_snow_a_m_L",
  "obs_mtn_snow_a_m_R", "obs_mtn_snow_a_m_cL0", "obs_mtn_snow_a_m_cL1", "obs_mtn_snow_a_m_cL2",
  "obs_mtn_snow_a_m_cL3", "obs_mtn_snow_a_m_cR0", "obs_mtn_snow_a_m_cR1",
  "obs_mtn_snow_a_m_cR2", "obs_mtn_snow_a_m_cR3", "obs_mtn_snow_a_m_cS0",
  "obs_mtn_snow_a_m_cS1", "obs_mtn_snow_a_m_cS2", "obs_mtn_snow_b0", "obs_mtn_snow_b1",
  "obs_mtn_snow_b2", "obs_mtn_snow_b3", "obs_mtn_snow_b_L", "obs_mtn_snow_b_R",
  "obs_mtn_snow_b_cL0", "obs_mtn_snow_b_cL1", "obs_mtn_snow_b_cL2", "obs_mtn_snow_b_cL3",
  "obs_mtn_snow_b_cR0", "obs_mtn_snow_b_cR1", "obs_mtn_snow_b_cR2", "obs_mtn_snow_b_cR3",
  "obs_mtn_snow_b_cS0", "obs_mtn_snow_b_cS1", "obs_mtn_snow_b_cS2", "obs_mtn_snow_b_cS3",
  "obs_mtn_snow_b_m0", "obs_mtn_snow_b_m1", "obs_mtn_snow_b_m2", "obs_mtn_snow_b_m3",
  "obs_mtn_snow_b_m_L", "obs_mtn_snow_b_m_R", "obs_mtn_snow_b_m_cL0", "obs_mtn_snow_b_m_cL1",
  "obs_mtn_snow_b_m_cL2", "obs_mtn_snow_b_m_cL3", "obs_mtn_snow_b_m_cR0",
  "obs_mtn_snow_b_m_cR1", "obs_mtn_snow_b_m_cR2", "obs_mtn_snow_b_m_cR3",
  "obs_mtn_snow_b_m_cS0", "obs_mtn_snow_b_m_cS1", "obs_mtn_snow_b_m_cS2", "obs_mtn_snow_c0",
  "obs_mtn_snow_c1", "obs_mtn_snow_c2", "obs_mtn_snow_c3", "obs_mtn_snow_c4",
  "obs_mtn_snow_c5", "obs_mtn_snow_c6", "obs_mtn_snow_c7", "obs_mtn_snow_c8",
  "obs_mtn_snow_c9", "obs_mtn_snow_c_L", "obs_mtn_snow_c_R", "obs_mtn_snow_c_cL0",
  "obs_mtn_snow_c_cL1", "obs_mtn_snow_c_cL2", "obs_mtn_snow_c_cL3", "obs_mtn_snow_c_cL5",
  "obs_mtn_snow_c_cL6", "obs_mtn_snow_c_cL7", "obs_mtn_snow_c_cL8", "obs_mtn_snow_c_cL9",
  "obs_mtn_snow_c_cR0", "obs_mtn_snow_c_cR1", "obs_mtn_snow_c_cR2", "obs_mtn_snow_c_cR3",
  "obs_mtn_snow_c_cR4", "obs_mtn_snow_c_cR5", "obs_mtn_snow_c_cR6", "obs_mtn_snow_c_cR7",
  "obs_mtn_snow_c_cR8", "obs_mtn_snow_c_cR9", "obs_mtn_snow_c_cS1", "obs_mtn_snow_c_cS2",
  "obs_mtn_snow_c_cS3", "obs_mtn_snow_c_cS4", "obs_mtn_snow_c_cS5", "obs_mtn_snow_c_cS6",
  "obs_mtn_snow_c_cS7", "obs_mtn_snow_c_cS8", "obs_mtn_snow_c_m0", "obs_mtn_snow_c_m1",
  "obs_mtn_snow_c_m2", "obs_mtn_snow_c_m3", "obs_mtn_snow_c_m4", "obs_mtn_snow_c_m5",
  "obs_mtn_snow_c_m6", "obs_mtn_snow_c_m7", "obs_mtn_snow_c_m8", "obs_mtn_snow_c_m9",
  "obs_mtn_snow_c_m_L", "obs_mtn_snow_c_m_R", "obs_mtn_snow_c_m_cL0", "obs_mtn_snow_c_m_cL1",
  "obs_mtn_snow_c_m_cL2", "obs_mtn_snow_c_m_cL3", "obs_mtn_snow_c_m_cL4",
  "obs_mtn_snow_c_m_cL5", "obs_mtn_snow_c_m_cL6", "obs_mtn_snow_c_m_cL7",
  "obs_mtn_snow_c_m_cL8", "obs_mtn_snow_c_m_cL9", "obs_mtn_snow_c_m_cR0",
  "obs_mtn_snow_c_m_cR1", "obs_mtn_snow_c_m_cR2", "obs_mtn_snow_c_m_cR3",
  "obs_mtn_snow_c_m_cR4", "obs_mtn_snow_c_m_cR5", "obs_mtn_snow_c_m_cR6",
  "obs_mtn_snow_c_m_cR7", "obs_mtn_snow_c_m_cR8", "obs_mtn_snow_c_m_cR9",
  "obs_mtn_snow_c_m_cS1", "obs_mtn_snow_c_m_cS2", "obs_mtn_snow_c_m_cS3",
  "obs_mtn_snow_c_m_cS4", "obs_mtn_snow_c_m_cS5", "obs_mtn_snow_c_m_cS6",
  "obs_mtn_snow_c_m_cS7", "obs_mtn_snow_c_m_cS8", "obs_mtn_snow_crag_a0",
  "obs_mtn_snow_crag_a1", "obs_mtn_snow_crag_a2", "obs_mtn_snow_crag_a3",
  "obs_mtn_snow_crag_a_L", "obs_mtn_snow_crag_a_R", "obs_mtn_snow_crag_a_cL0",
  "obs_mtn_snow_crag_a_cL1", "obs_mtn_snow_crag_a_cL2", "obs_mtn_snow_crag_a_cL3",
  "obs_mtn_snow_crag_a_cR0", "obs_mtn_snow_crag_a_cR1", "obs_mtn_snow_crag_a_cR2",
  "obs_mtn_snow_crag_a_cR3", "obs_mtn_snow_crag_a_cS0", "obs_mtn_snow_crag_a_cS1",
  "obs_mtn_snow_crag_a_cS2", "obs_mtn_snow_crag_a_cS3", "obs_mtn_snow_crag_a_m0",
  "obs_mtn_snow_crag_a_m1", "obs_mtn_snow_crag_a_m2", "obs_mtn_snow_crag_a_m3",
  "obs_mtn_snow_crag_a_m_L", "obs_mtn_snow_crag_a_m_R", "obs_mtn_snow_crag_a_m_cL0",
  "obs_mtn_snow_crag_a_m_cL1", "obs_mtn_snow_crag_a_m_cL2", "obs_mtn_snow_crag_a_m_cL3",
  "obs_mtn_snow_crag_a_m_cR0", "obs_mtn_snow_crag_a_m_cR1", "obs_mtn_snow_crag_a_m_cR2",
  "obs_mtn_snow_crag_a_m_cR3", "obs_mtn_snow_crag_a_m_cS0", "obs_mtn_snow_crag_a_m_cS1",
  "obs_mtn_snow_crag_a_m_cS2", "obs_mtn_snow_crag_a_m_cS3", "obs_mtn_snow_crag_b0",
  "obs_mtn_snow_crag_b1", "obs_mtn_snow_crag_b2", "obs_mtn_snow_crag_b3",
  "obs_mtn_snow_crag_b_L", "obs_mtn_snow_crag_b_R", "obs_mtn_snow_crag_b_cL0",
  "obs_mtn_snow_crag_b_cL1", "obs_mtn_snow_crag_b_cL2", "obs_mtn_snow_crag_b_cL3",
  "obs_mtn_snow_crag_b_cR0", "obs_mtn_snow_crag_b_cR1", "obs_mtn_snow_crag_b_cR2",
  "obs_mtn_snow_crag_b_cR3", "obs_mtn_snow_crag_b_cS1", "obs_mtn_snow_crag_b_cS2",
  "obs_mtn_snow_crag_b_cS3", "obs_mtn_snow_crag_b_m0", "obs_mtn_snow_crag_b_m1",
  "obs_mtn_snow_crag_b_m2", "obs_mtn_snow_crag_b_m3", "obs_mtn_snow_crag_b_m_L",
  "obs_mtn_snow_crag_b_m_R", "obs_mtn_snow_crag_b_m_cL0", "obs_mtn_snow_crag_b_m_cL1",
  "obs_mtn_snow_crag_b_m_cL2", "obs_mtn_snow_crag_b_m_cL3", "obs_mtn_snow_crag_b_m_cR0",
  "obs_mtn_snow_crag_b_m_cR1", "obs_mtn_snow_crag_b_m_cR2", "obs_mtn_snow_crag_b_m_cR3",
  "obs_mtn_snow_crag_b_m_cS0", "obs_mtn_snow_crag_b_m_cS1", "obs_mtn_snow_crag_b_m_cS2",
  "obs_mtn_snow_crag_c0", "obs_mtn_snow_crag_c1", "obs_mtn_snow_crag_c2",
  "obs_mtn_snow_crag_c3", "obs_mtn_snow_crag_c4", "obs_mtn_snow_crag_c5",
  "obs_mtn_snow_crag_c6", "obs_mtn_snow_crag_c7", "obs_mtn_snow_crag_c8",
  "obs_mtn_snow_crag_c9", "obs_mtn_snow_crag_c_L", "obs_mtn_snow_crag_c_R",
  "obs_mtn_snow_crag_c_cL0", "obs_mtn_snow_crag_c_cL1", "obs_mtn_snow_crag_c_cL2",
  "obs_mtn_snow_crag_c_cL3", "obs_mtn_snow_crag_c_cL4", "obs_mtn_snow_crag_c_cL5",
  "obs_mtn_snow_crag_c_cL6", "obs_mtn_snow_crag_c_cL7", "obs_mtn_snow_crag_c_cL8",
  "obs_mtn_snow_crag_c_cL9", "obs_mtn_snow_crag_c_cR0", "obs_mtn_snow_crag_c_cR1",
  "obs_mtn_snow_crag_c_cR2", "obs_mtn_snow_crag_c_cR3", "obs_mtn_snow_crag_c_cR4",
  "obs_mtn_snow_crag_c_cR5", "obs_mtn_snow_crag_c_cR6", "obs_mtn_snow_crag_c_cR7",
  "obs_mtn_snow_crag_c_cR8", "obs_mtn_snow_crag_c_cR9", "obs_mtn_snow_crag_c_cS1",
  "obs_mtn_snow_crag_c_cS2", "obs_mtn_snow_crag_c_cS3", "obs_mtn_snow_crag_c_cS4",
  "obs_mtn_snow_crag_c_cS5", "obs_mtn_snow_crag_c_cS6", "obs_mtn_snow_crag_c_cS7",
  "obs_mtn_snow_crag_c_m0", "obs_mtn_snow_crag_c_m1", "obs_mtn_snow_crag_c_m2",
  "obs_mtn_snow_crag_c_m3", "obs_mtn_snow_crag_c_m4", "obs_mtn_snow_crag_c_m5",
  "obs_mtn_snow_crag_c_m6", "obs_mtn_snow_crag_c_m7", "obs_mtn_snow_crag_c_m8",
  "obs_mtn_snow_crag_c_m9", "obs_mtn_snow_crag_c_m_L", "obs_mtn_snow_crag_c_m_R",
  "obs_mtn_snow_crag_c_m_cL0", "obs_mtn_snow_crag_c_m_cL1", "obs_mtn_snow_crag_c_m_cL2",
  "obs_mtn_snow_crag_c_m_cL3", "obs_mtn_snow_crag_c_m_cL4", "obs_mtn_snow_crag_c_m_cL5",
  "obs_mtn_snow_crag_c_m_cL6", "obs_mtn_snow_crag_c_m_cL7", "obs_mtn_snow_crag_c_m_cL8",
  "obs_mtn_snow_crag_c_m_cL9", "obs_mtn_snow_crag_c_m_cR0", "obs_mtn_snow_crag_c_m_cR1",
  "obs_mtn_snow_crag_c_m_cR2", "obs_mtn_snow_crag_c_m_cR3", "obs_mtn_snow_crag_c_m_cR4",
  "obs_mtn_snow_crag_c_m_cR5", "obs_mtn_snow_crag_c_m_cR6", "obs_mtn_snow_crag_c_m_cR7",
  "obs_mtn_snow_crag_c_m_cR8", "obs_mtn_snow_crag_c_m_cR9", "obs_mtn_snow_crag_c_m_cS2",
  "obs_mtn_snow_crag_c_m_cS3", "obs_mtn_snow_crag_c_m_cS4", "obs_mtn_snow_crag_c_m_cS5",
  "obs_mtn_snow_crag_c_m_cS6", "obs_mtn_snow_crag_c_m_cS7", "obs_mtn_snow_crag_c_m_cS8",
  "obs_mtn_snow_mass_a0", "obs_mtn_snow_mass_a1", "obs_mtn_snow_mass_a_L",
  "obs_mtn_snow_mass_a_R", "obs_mtn_snow_mass_a_cL0", "obs_mtn_snow_mass_a_cL1",
  "obs_mtn_snow_mass_a_cR0", "obs_mtn_snow_mass_a_cR1", "obs_mtn_snow_mass_a_cS0",
  "obs_mtn_snow_mass_a_cS1", "obs_mtn_snow_mass_a_m0", "obs_mtn_snow_mass_a_m1",
  "obs_mtn_snow_mass_a_m_L", "obs_mtn_snow_mass_a_m_R", "obs_mtn_snow_mass_a_m_cL0",
  "obs_mtn_snow_mass_a_m_cL1", "obs_mtn_snow_mass_a_m_cR0", "obs_mtn_snow_mass_a_m_cR1",
  "obs_mtn_snow_mass_a_m_cS0", "obs_mtn_snow_mass_a_m_cS1", "obs_mtn_snow_mass_b0",
  "obs_mtn_snow_mass_b1", "obs_mtn_snow_mass_b2", "obs_mtn_snow_mass_b3",
  "obs_mtn_snow_mass_b_L", "obs_mtn_snow_mass_b_R", "obs_mtn_snow_mass_b_cL0",
  "obs_mtn_snow_mass_b_cL1", "obs_mtn_snow_mass_b_cL2", "obs_mtn_snow_mass_b_cL3",
  "obs_mtn_snow_mass_b_cR0", "obs_mtn_snow_mass_b_cR1", "obs_mtn_snow_mass_b_cR2",
  "obs_mtn_snow_mass_b_cR3", "obs_mtn_snow_mass_b_cS0", "obs_mtn_snow_mass_b_cS1",
  "obs_mtn_snow_mass_b_cS2", "obs_mtn_snow_mass_b_cS3", "obs_mtn_snow_mass_b_m0",
  "obs_mtn_snow_mass_b_m1", "obs_mtn_snow_mass_b_m2", "obs_mtn_snow_mass_b_m3",
  "obs_mtn_snow_mass_b_m_L", "obs_mtn_snow_mass_b_m_R", "obs_mtn_snow_mass_b_m_cL0",
  "obs_mtn_snow_mass_b_m_cL1", "obs_mtn_snow_mass_b_m_cL2", "obs_mtn_snow_mass_b_m_cL3",
  "obs_mtn_snow_mass_b_m_cR0", "obs_mtn_snow_mass_b_m_cR1", "obs_mtn_snow_mass_b_m_cR2",
  "obs_mtn_snow_mass_b_m_cR3", "obs_mtn_snow_mass_b_m_cS0", "obs_mtn_snow_mass_b_m_cS1",
  "obs_mtn_snow_mass_b_m_cS2", "obs_mtn_snow_mass_b_m_cS3", "obs_mtn_snow_mass_c0",
  "obs_mtn_snow_mass_c1", "obs_mtn_snow_mass_c2", "obs_mtn_snow_mass_c_L",
  "obs_mtn_snow_mass_c_R", "obs_mtn_snow_mass_c_cL0", "obs_mtn_snow_mass_c_cL1",
  "obs_mtn_snow_mass_c_cL2", "obs_mtn_snow_mass_c_cR0", "obs_mtn_snow_mass_c_cR1",
  "obs_mtn_snow_mass_c_cR2", "obs_mtn_snow_mass_c_cS0", "obs_mtn_snow_mass_c_cS1",
  "obs_mtn_snow_mass_c_cS2", "obs_mtn_snow_mass_c_m0", "obs_mtn_snow_mass_c_m1",
  "obs_mtn_snow_mass_c_m2", "obs_mtn_snow_mass_c_m_L", "obs_mtn_snow_mass_c_m_R",
  "obs_mtn_snow_mass_c_m_cL0", "obs_mtn_snow_mass_c_m_cL1", "obs_mtn_snow_mass_c_m_cL2",
  "obs_mtn_snow_mass_c_m_cR0", "obs_mtn_snow_mass_c_m_cR1", "obs_mtn_snow_mass_c_m_cR2",
  "obs_mtn_snow_mass_c_m_cS0", "obs_mtn_snow_mass_c_m_cS1", "obs_mtn_snow_mass_c_m_cS2",
  "obs_mtn_snow_mass_d0", "obs_mtn_snow_mass_d_L", "obs_mtn_snow_mass_d_R",
  "obs_mtn_snow_mass_d_cL0", "obs_mtn_snow_mass_d_cR0", "obs_mtn_snow_mass_d_cS0",
  "obs_mtn_snow_mass_d_m0", "obs_mtn_snow_mass_d_m_L", "obs_mtn_snow_mass_d_m_R",
  "obs_mtn_snow_mass_d_m_cL0", "obs_mtn_snow_mass_d_m_cR0", "obs_mtn_snow_mass_d_m_cS0",
  "obs_mtn_snow_mass_e0", "obs_mtn_snow_mass_e1", "obs_mtn_snow_mass_e2",
  "obs_mtn_snow_mass_e3", "obs_mtn_snow_mass_e4", "obs_mtn_snow_mass_e_L",
  "obs_mtn_snow_mass_e_R", "obs_mtn_snow_mass_e_cL0", "obs_mtn_snow_mass_e_cL1",
  "obs_mtn_snow_mass_e_cL2", "obs_mtn_snow_mass_e_cL3", "obs_mtn_snow_mass_e_cL4",
  "obs_mtn_snow_mass_e_cR0", "obs_mtn_snow_mass_e_cR1", "obs_mtn_snow_mass_e_cR2",
  "obs_mtn_snow_mass_e_cR3", "obs_mtn_snow_mass_e_cR4", "obs_mtn_snow_mass_e_cS0",
  "obs_mtn_snow_mass_e_cS1", "obs_mtn_snow_mass_e_cS2", "obs_mtn_snow_mass_e_cS3",
  "obs_mtn_snow_mass_e_cS4", "obs_mtn_snow_mass_e_m0", "obs_mtn_snow_mass_e_m1",
  "obs_mtn_snow_mass_e_m2", "obs_mtn_snow_mass_e_m3", "obs_mtn_snow_mass_e_m4",
  "obs_mtn_snow_mass_e_m_L", "obs_mtn_snow_mass_e_m_R", "obs_mtn_snow_mass_e_m_cL0",
  "obs_mtn_snow_mass_e_m_cL1", "obs_mtn_snow_mass_e_m_cL2", "obs_mtn_snow_mass_e_m_cL3",
  "obs_mtn_snow_mass_e_m_cL4", "obs_mtn_snow_mass_e_m_cR0", "obs_mtn_snow_mass_e_m_cR1",
  "obs_mtn_snow_mass_e_m_cR2", "obs_mtn_snow_mass_e_m_cR3", "obs_mtn_snow_mass_e_m_cR4",
  "obs_mtn_snow_mass_e_m_cS0", "obs_mtn_snow_mass_e_m_cS1", "obs_mtn_snow_mass_e_m_cS2",
  "obs_mtn_snow_mass_e_m_cS3", "obs_mtn_snow_mass_e_m_cS4", "obs_mtn_snow_mass_f0",
  "obs_mtn_snow_mass_f1", "obs_mtn_snow_mass_f_L", "obs_mtn_snow_mass_f_R",
  "obs_mtn_snow_mass_f_cL0", "obs_mtn_snow_mass_f_cL1", "obs_mtn_snow_mass_f_cR0",
  "obs_mtn_snow_mass_f_cR1", "obs_mtn_snow_mass_f_cS0", "obs_mtn_snow_mass_f_cS1",
  "obs_mtn_snow_mass_f_m0", "obs_mtn_snow_mass_f_m1", "obs_mtn_snow_mass_f_m_L",
  "obs_mtn_snow_mass_f_m_R", "obs_mtn_snow_mass_f_m_cL0", "obs_mtn_snow_mass_f_m_cL1",
  "obs_mtn_snow_mass_f_m_cR0", "obs_mtn_snow_mass_f_m_cR1", "obs_mtn_snow_mass_f_m_cS0",
  "obs_mtn_snow_mass_f_m_cS1", "obs_mtn_snow_mass_g0", "obs_mtn_snow_mass_g1",
  "obs_mtn_snow_mass_g2", "obs_mtn_snow_mass_g3", "obs_mtn_snow_mass_g4",
  "obs_mtn_snow_mass_g5", "obs_mtn_snow_mass_g_L", "obs_mtn_snow_mass_g_R",
  "obs_mtn_snow_mass_g_cL0", "obs_mtn_snow_mass_g_cL1", "obs_mtn_snow_mass_g_cL2",
  "obs_mtn_snow_mass_g_cL3", "obs_mtn_snow_mass_g_cL4", "obs_mtn_snow_mass_g_cL5",
  "obs_mtn_snow_mass_g_cR0", "obs_mtn_snow_mass_g_cR1", "obs_mtn_snow_mass_g_cR2",
  "obs_mtn_snow_mass_g_cR3", "obs_mtn_snow_mass_g_cR4", "obs_mtn_snow_mass_g_cR5",
  "obs_mtn_snow_mass_g_cS0", "obs_mtn_snow_mass_g_cS1", "obs_mtn_snow_mass_g_cS2",
  "obs_mtn_snow_mass_g_cS3", "obs_mtn_snow_mass_g_cS4", "obs_mtn_snow_mass_g_m0",
  "obs_mtn_snow_mass_g_m1", "obs_mtn_snow_mass_g_m2", "obs_mtn_snow_mass_g_m3",
  "obs_mtn_snow_mass_g_m4", "obs_mtn_snow_mass_g_m5", "obs_mtn_snow_mass_g_m_L",
  "obs_mtn_snow_mass_g_m_R", "obs_mtn_snow_mass_g_m_cL0", "obs_mtn_snow_mass_g_m_cL1",
  "obs_mtn_snow_mass_g_m_cL2", "obs_mtn_snow_mass_g_m_cL3", "obs_mtn_snow_mass_g_m_cL4",
  "obs_mtn_snow_mass_g_m_cL5", "obs_mtn_snow_mass_g_m_cR0", "obs_mtn_snow_mass_g_m_cR1",
  "obs_mtn_snow_mass_g_m_cR2", "obs_mtn_snow_mass_g_m_cR3", "obs_mtn_snow_mass_g_m_cR4",
  "obs_mtn_snow_mass_g_m_cR5", "obs_mtn_snow_mass_g_m_cS1", "obs_mtn_snow_mass_g_m_cS2",
  "obs_mtn_snow_mass_g_m_cS3", "obs_mtn_snow_mass_g_m_cS4", "obs_mtn_snow_mass_g_m_cS5",
  "obs_mtn_snow_mass_h0", "obs_mtn_snow_mass_h1", "obs_mtn_snow_mass_h_L",
  "obs_mtn_snow_mass_h_R", "obs_mtn_snow_mass_h_cL0", "obs_mtn_snow_mass_h_cL1",
  "obs_mtn_snow_mass_h_cR0", "obs_mtn_snow_mass_h_cR1", "obs_mtn_snow_mass_h_cS0",
  "obs_mtn_snow_mass_h_cS1", "obs_mtn_snow_mass_h_m0", "obs_mtn_snow_mass_h_m1",
  "obs_mtn_snow_mass_h_m_L", "obs_mtn_snow_mass_h_m_R", "obs_mtn_snow_mass_h_m_cL0",
  "obs_mtn_snow_mass_h_m_cL1", "obs_mtn_snow_mass_h_m_cR0", "obs_mtn_snow_mass_h_m_cR1",
  "obs_mtn_snow_mass_h_m_cS0", "obs_mtn_snow_mass_h_m_cS1", "obs_mtn_snow_mass_i0",
  "obs_mtn_snow_mass_i1", "obs_mtn_snow_mass_i2", "obs_mtn_snow_mass_i3",
  "obs_mtn_snow_mass_i4", "obs_mtn_snow_mass_i5", "obs_mtn_snow_mass_i_L",
  "obs_mtn_snow_mass_i_R", "obs_mtn_snow_mass_i_cL0", "obs_mtn_snow_mass_i_cL1",
  "obs_mtn_snow_mass_i_cL2", "obs_mtn_snow_mass_i_cL3", "obs_mtn_snow_mass_i_cL4",
  "obs_mtn_snow_mass_i_cL5", "obs_mtn_snow_mass_i_cR0", "obs_mtn_snow_mass_i_cR1",
  "obs_mtn_snow_mass_i_cR2", "obs_mtn_snow_mass_i_cR3", "obs_mtn_snow_mass_i_cR4",
  "obs_mtn_snow_mass_i_cR5", "obs_mtn_snow_mass_i_cS0", "obs_mtn_snow_mass_i_cS1",
  "obs_mtn_snow_mass_i_cS2", "obs_mtn_snow_mass_i_cS3", "obs_mtn_snow_mass_i_cS4",
  "obs_mtn_snow_mass_i_m0", "obs_mtn_snow_mass_i_m1", "obs_mtn_snow_mass_i_m2",
  "obs_mtn_snow_mass_i_m3", "obs_mtn_snow_mass_i_m4", "obs_mtn_snow_mass_i_m5",
  "obs_mtn_snow_mass_i_m_L", "obs_mtn_snow_mass_i_m_R", "obs_mtn_snow_mass_i_m_cL0",
  "obs_mtn_snow_mass_i_m_cL1", "obs_mtn_snow_mass_i_m_cL2", "obs_mtn_snow_mass_i_m_cL3",
  "obs_mtn_snow_mass_i_m_cL4", "obs_mtn_snow_mass_i_m_cL5", "obs_mtn_snow_mass_i_m_cR0",
  "obs_mtn_snow_mass_i_m_cR1", "obs_mtn_snow_mass_i_m_cR2", "obs_mtn_snow_mass_i_m_cR3",
  "obs_mtn_snow_mass_i_m_cR4", "obs_mtn_snow_mass_i_m_cR5", "obs_mtn_snow_mass_i_m_cS1",
  "obs_mtn_snow_mass_i_m_cS2", "obs_mtn_snow_mass_i_m_cS3", "obs_mtn_snow_mass_i_m_cS4",
  "obs_mtn_snow_mass_i_m_cS5", "obs_mtn_snow_mass_j0", "obs_mtn_snow_mass_j1",
  "obs_mtn_snow_mass_j2", "obs_mtn_snow_mass_j_L", "obs_mtn_snow_mass_j_R",
  "obs_mtn_snow_mass_j_cL0", "obs_mtn_snow_mass_j_cL1", "obs_mtn_snow_mass_j_cL2",
  "obs_mtn_snow_mass_j_cR0", "obs_mtn_snow_mass_j_cR1", "obs_mtn_snow_mass_j_cR2",
  "obs_mtn_snow_mass_j_cS0", "obs_mtn_snow_mass_j_cS1", "obs_mtn_snow_mass_j_cS2",
  "obs_mtn_snow_mass_j_m0", "obs_mtn_snow_mass_j_m1", "obs_mtn_snow_mass_j_m2",
  "obs_mtn_snow_mass_j_m_L", "obs_mtn_snow_mass_j_m_R", "obs_mtn_snow_mass_j_m_cL0",
  "obs_mtn_snow_mass_j_m_cL1", "obs_mtn_snow_mass_j_m_cL2", "obs_mtn_snow_mass_j_m_cR0",
  "obs_mtn_snow_mass_j_m_cR1", "obs_mtn_snow_mass_j_m_cR2", "obs_mtn_snow_mass_j_m_cS0",
  "obs_mtn_snow_mass_j_m_cS1", "obs_mtn_snow_mass_j_m_cS2", "obs_mtn_snow_range_e0",
  "obs_mtn_snow_range_e1", "obs_mtn_snow_range_e2", "obs_mtn_snow_range_e3",
  "obs_mtn_snow_range_e4", "obs_mtn_snow_range_e5", "obs_mtn_snow_range_e6",
  "obs_mtn_snow_range_e7", "obs_mtn_snow_range_e8", "obs_mtn_snow_range_e_L",
  "obs_mtn_snow_range_e_R", "obs_mtn_snow_range_e_cL0", "obs_mtn_snow_range_e_cL1",
  "obs_mtn_snow_range_e_cL2", "obs_mtn_snow_range_e_cL3", "obs_mtn_snow_range_e_cL4",
  "obs_mtn_snow_range_e_cL5", "obs_mtn_snow_range_e_cL6", "obs_mtn_snow_range_e_cL7",
  "obs_mtn_snow_range_e_cL8", "obs_mtn_snow_range_e_cR0", "obs_mtn_snow_range_e_cR1",
  "obs_mtn_snow_range_e_cR2", "obs_mtn_snow_range_e_cR3", "obs_mtn_snow_range_e_cR4",
  "obs_mtn_snow_range_e_cR5", "obs_mtn_snow_range_e_cR6", "obs_mtn_snow_range_e_cR7",
  "obs_mtn_snow_range_e_cS0", "obs_mtn_snow_range_e_cS1", "obs_mtn_snow_range_e_cS2",
  "obs_mtn_snow_range_e_cS3", "obs_mtn_snow_range_e_cS4", "obs_mtn_snow_range_e_cS5",
  "obs_mtn_snow_range_e_cS6", "obs_mtn_snow_range_e_cS7", "obs_mtn_snow_range_e_cS8",
  "obs_mtn_snow_range_e_m0", "obs_mtn_snow_range_e_m1", "obs_mtn_snow_range_e_m2",
  "obs_mtn_snow_range_e_m3", "obs_mtn_snow_range_e_m4", "obs_mtn_snow_range_e_m5",
  "obs_mtn_snow_range_e_m6", "obs_mtn_snow_range_e_m7", "obs_mtn_snow_range_e_m8",
  "obs_mtn_snow_range_e_m_L", "obs_mtn_snow_range_e_m_R", "obs_mtn_snow_range_e_m_cL0",
  "obs_mtn_snow_range_e_m_cL1", "obs_mtn_snow_range_e_m_cL2", "obs_mtn_snow_range_e_m_cL3",
  "obs_mtn_snow_range_e_m_cL4", "obs_mtn_snow_range_e_m_cL5", "obs_mtn_snow_range_e_m_cL6",
  "obs_mtn_snow_range_e_m_cL7", "obs_mtn_snow_range_e_m_cL8", "obs_mtn_snow_range_e_m_cR0",
  "obs_mtn_snow_range_e_m_cR1", "obs_mtn_snow_range_e_m_cR2", "obs_mtn_snow_range_e_m_cR3",
  "obs_mtn_snow_range_e_m_cR4", "obs_mtn_snow_range_e_m_cR5", "obs_mtn_snow_range_e_m_cR6",
  "obs_mtn_snow_range_e_m_cR7", "obs_mtn_snow_range_e_m_cR8", "obs_mtn_snow_range_e_m_cS0",
  "obs_mtn_snow_range_e_m_cS1", "obs_mtn_snow_range_e_m_cS2", "obs_mtn_snow_range_e_m_cS3",
  "obs_mtn_snow_range_e_m_cS4", "obs_mtn_snow_range_e_m_cS5", "obs_mtn_snow_range_e_m_cS6",
  "obs_mtn_snow_range_e_m_cS7", "obs_mtn_snow_range_e_m_cS8", "obs_mtn_swamp_a0",
  "obs_mtn_swamp_a1", "obs_mtn_swamp_a2", "obs_mtn_swamp_a3", "obs_mtn_swamp_a_L",
  "obs_mtn_swamp_a_R", "obs_mtn_swamp_a_cL0", "obs_mtn_swamp_a_cL1", "obs_mtn_swamp_a_cL2",
  "obs_mtn_swamp_a_cL3", "obs_mtn_swamp_a_cR0", "obs_mtn_swamp_a_cR1", "obs_mtn_swamp_a_cR2",
  "obs_mtn_swamp_a_cR3", "obs_mtn_swamp_a_cS0", "obs_mtn_swamp_a_cS1", "obs_mtn_swamp_a_cS2",
  "obs_mtn_swamp_a_cS3", "obs_mtn_swamp_a_m0", "obs_mtn_swamp_a_m1", "obs_mtn_swamp_a_m2",
  "obs_mtn_swamp_a_m3", "obs_mtn_swamp_a_m_L", "obs_mtn_swamp_a_m_R", "obs_mtn_swamp_a_m_cL0",
  "obs_mtn_swamp_a_m_cL1", "obs_mtn_swamp_a_m_cL2", "obs_mtn_swamp_a_m_cR0",
  "obs_mtn_swamp_a_m_cR1", "obs_mtn_swamp_a_m_cR2", "obs_mtn_swamp_a_m_cR3",
  "obs_mtn_swamp_a_m_cS0", "obs_mtn_swamp_a_m_cS1", "obs_mtn_swamp_a_m_cS2",
  "obs_mtn_swamp_a_m_cS3", "obs_mtn_swamp_b0", "obs_mtn_swamp_b1", "obs_mtn_swamp_b2",
  "obs_mtn_swamp_b3", "obs_mtn_swamp_b4", "obs_mtn_swamp_b5", "obs_mtn_swamp_b6",
  "obs_mtn_swamp_b_L", "obs_mtn_swamp_b_R", "obs_mtn_swamp_b_cL0", "obs_mtn_swamp_b_cL1",
  "obs_mtn_swamp_b_cL2", "obs_mtn_swamp_b_cL3", "obs_mtn_swamp_b_cL4", "obs_mtn_swamp_b_cL5",
  "obs_mtn_swamp_b_cL6", "obs_mtn_swamp_b_cR0", "obs_mtn_swamp_b_cR1", "obs_mtn_swamp_b_cR2",
  "obs_mtn_swamp_b_cR3", "obs_mtn_swamp_b_cR4", "obs_mtn_swamp_b_cR5", "obs_mtn_swamp_b_cR6",
  "obs_mtn_swamp_b_cS0", "obs_mtn_swamp_b_cS1", "obs_mtn_swamp_b_cS2", "obs_mtn_swamp_b_cS3",
  "obs_mtn_swamp_b_cS4", "obs_mtn_swamp_b_cS5", "obs_mtn_swamp_b_cS6", "obs_mtn_swamp_b_m0",
  "obs_mtn_swamp_b_m1", "obs_mtn_swamp_b_m2", "obs_mtn_swamp_b_m3", "obs_mtn_swamp_b_m4",
  "obs_mtn_swamp_b_m5", "obs_mtn_swamp_b_m6", "obs_mtn_swamp_b_m_L", "obs_mtn_swamp_b_m_R",
  "obs_mtn_swamp_b_m_cL0", "obs_mtn_swamp_b_m_cL1", "obs_mtn_swamp_b_m_cL2",
  "obs_mtn_swamp_b_m_cL3", "obs_mtn_swamp_b_m_cL4", "obs_mtn_swamp_b_m_cL5",
  "obs_mtn_swamp_b_m_cL6", "obs_mtn_swamp_b_m_cR0", "obs_mtn_swamp_b_m_cR1",
  "obs_mtn_swamp_b_m_cR2", "obs_mtn_swamp_b_m_cR3", "obs_mtn_swamp_b_m_cR4",
  "obs_mtn_swamp_b_m_cR5", "obs_mtn_swamp_b_m_cR6", "obs_mtn_swamp_b_m_cS0",
  "obs_mtn_swamp_b_m_cS1", "obs_mtn_swamp_b_m_cS2", "obs_mtn_swamp_b_m_cS3",
  "obs_mtn_swamp_b_m_cS4", "obs_mtn_swamp_b_m_cS5", "obs_mtn_swamp_b_m_cS6",
  "obs_mtn_tall_a_B", "obs_mtn_tall_a_T", "obs_mtn_tall_a_cB0", "obs_mtn_tall_a_cB1",
  "obs_mtn_tall_a_cB2", "obs_mtn_tall_a_cB3", "obs_mtn_tall_a_cB4", "obs_mtn_tall_a_cB5",
  "obs_mtn_tall_a_cT0", "obs_mtn_tall_a_cT1", "obs_mtn_tall_a_cT2", "obs_mtn_tall_a_cT3",
  "obs_mtn_tall_a_cT4", "obs_mtn_tall_a_cT5", "obs_mtn_tall_a_m_B", "obs_mtn_tall_a_m_T",
  "obs_mtn_tall_a_m_cB0", "obs_mtn_tall_a_m_cB1", "obs_mtn_tall_a_m_cB2",
  "obs_mtn_tall_a_m_cB3", "obs_mtn_tall_a_m_cB4", "obs_mtn_tall_a_m_cB5",
  "obs_mtn_tall_a_m_cT0", "obs_mtn_tall_a_m_cT1", "obs_mtn_tall_a_m_cT2",
  "obs_mtn_tall_a_m_cT3", "obs_mtn_tall_a_m_cT4", "obs_mtn_tall_a_m_cT5",
  "obs_mtn_tall_a_m_r0", "obs_mtn_tall_a_m_r1", "obs_mtn_tall_a_m_r2", "obs_mtn_tall_a_m_r3",
  "obs_mtn_tall_a_m_r4", "obs_mtn_tall_a_m_r5", "obs_mtn_tall_a_r0", "obs_mtn_tall_a_r1",
  "obs_mtn_tall_a_r2", "obs_mtn_tall_a_r3", "obs_mtn_tall_a_r4", "obs_mtn_tall_a_r5",
  "obs_mtn_tall_b_B", "obs_mtn_tall_b_T", "obs_mtn_tall_b_cB0", "obs_mtn_tall_b_cB1",
  "obs_mtn_tall_b_cB2", "obs_mtn_tall_b_cB3", "obs_mtn_tall_b_cB4", "obs_mtn_tall_b_cT0",
  "obs_mtn_tall_b_cT1", "obs_mtn_tall_b_cT2", "obs_mtn_tall_b_cT3", "obs_mtn_tall_b_cT4",
  "obs_mtn_tall_b_m_B", "obs_mtn_tall_b_m_T", "obs_mtn_tall_b_m_cB0", "obs_mtn_tall_b_m_cB1",
  "obs_mtn_tall_b_m_cB2", "obs_mtn_tall_b_m_cB3", "obs_mtn_tall_b_m_cB4",
  "obs_mtn_tall_b_m_cT0", "obs_mtn_tall_b_m_cT1", "obs_mtn_tall_b_m_cT2",
  "obs_mtn_tall_b_m_cT3", "obs_mtn_tall_b_m_cT4", "obs_mtn_tall_b_m_r0", "obs_mtn_tall_b_m_r1",
  "obs_mtn_tall_b_m_r2", "obs_mtn_tall_b_m_r3", "obs_mtn_tall_b_m_r4", "obs_mtn_tall_b_r0",
  "obs_mtn_tall_b_r1", "obs_mtn_tall_b_r2", "obs_mtn_tall_b_r3", "obs_mtn_tall_b_r4",
  "obs_mtn_tall_lava_B", "obs_mtn_tall_lava_T", "obs_mtn_tall_lava_cB0",
  "obs_mtn_tall_lava_cB1", "obs_mtn_tall_lava_cB2", "obs_mtn_tall_lava_cB3",
  "obs_mtn_tall_lava_cB4", "obs_mtn_tall_lava_cB5", "obs_mtn_tall_lava_cB6",
  "obs_mtn_tall_lava_cT0", "obs_mtn_tall_lava_cT1", "obs_mtn_tall_lava_cT2",
  "obs_mtn_tall_lava_cT3", "obs_mtn_tall_lava_cT4", "obs_mtn_tall_lava_cT5",
  "obs_mtn_tall_lava_cT6", "obs_mtn_tall_lava_m_B", "obs_mtn_tall_lava_m_T",
  "obs_mtn_tall_lava_m_cB0", "obs_mtn_tall_lava_m_cB1", "obs_mtn_tall_lava_m_cB2",
  "obs_mtn_tall_lava_m_cB3", "obs_mtn_tall_lava_m_cB4", "obs_mtn_tall_lava_m_cB5",
  "obs_mtn_tall_lava_m_cB6", "obs_mtn_tall_lava_m_cT0", "obs_mtn_tall_lava_m_cT1",
  "obs_mtn_tall_lava_m_cT2", "obs_mtn_tall_lava_m_cT3", "obs_mtn_tall_lava_m_cT4",
  "obs_mtn_tall_lava_m_cT5", "obs_mtn_tall_lava_m_cT6", "obs_mtn_tall_lava_m_r0",
  "obs_mtn_tall_lava_m_r1", "obs_mtn_tall_lava_m_r2", "obs_mtn_tall_lava_m_r3",
  "obs_mtn_tall_lava_m_r4", "obs_mtn_tall_lava_m_r5", "obs_mtn_tall_lava_m_r6",
  "obs_mtn_tall_lava_r0", "obs_mtn_tall_lava_r1", "obs_mtn_tall_lava_r2",
  "obs_mtn_tall_lava_r3", "obs_mtn_tall_lava_r4", "obs_mtn_tall_lava_r5",
  "obs_mtn_tall_lava_r6", "obs_mtn_tall_snow_B", "obs_mtn_tall_snow_T",
  "obs_mtn_tall_snow_cB0", "obs_mtn_tall_snow_cB1", "obs_mtn_tall_snow_cB2",
  "obs_mtn_tall_snow_cB3", "obs_mtn_tall_snow_cB4", "obs_mtn_tall_snow_cB5",
  "obs_mtn_tall_snow_cT0", "obs_mtn_tall_snow_cT1", "obs_mtn_tall_snow_cT2",
  "obs_mtn_tall_snow_cT3", "obs_mtn_tall_snow_cT4", "obs_mtn_tall_snow_cT5",
  "obs_mtn_tall_snow_m_B", "obs_mtn_tall_snow_m_T", "obs_mtn_tall_snow_m_cB0",
  "obs_mtn_tall_snow_m_cB1", "obs_mtn_tall_snow_m_cB2", "obs_mtn_tall_snow_m_cB3",
  "obs_mtn_tall_snow_m_cB4", "obs_mtn_tall_snow_m_cB5", "obs_mtn_tall_snow_m_cT0",
  "obs_mtn_tall_snow_m_cT1", "obs_mtn_tall_snow_m_cT2", "obs_mtn_tall_snow_m_cT3",
  "obs_mtn_tall_snow_m_cT4", "obs_mtn_tall_snow_m_cT5", "obs_mtn_tall_snow_m_r0",
  "obs_mtn_tall_snow_m_r1", "obs_mtn_tall_snow_m_r2", "obs_mtn_tall_snow_m_r3",
  "obs_mtn_tall_snow_m_r4", "obs_mtn_tall_snow_m_r5", "obs_mtn_tall_snow_r0",
  "obs_mtn_tall_snow_r1", "obs_mtn_tall_snow_r2", "obs_mtn_tall_snow_r3",
  "obs_mtn_tall_snow_r4", "obs_mtn_tall_snow_r5", "obs_mtn_tall_swamp_B",
  "obs_mtn_tall_swamp_T", "obs_mtn_tall_swamp_cB0", "obs_mtn_tall_swamp_cB1",
  "obs_mtn_tall_swamp_cB2", "obs_mtn_tall_swamp_cB3", "obs_mtn_tall_swamp_cB4",
  "obs_mtn_tall_swamp_cB5", "obs_mtn_tall_swamp_cB6", "obs_mtn_tall_swamp_cT0",
  "obs_mtn_tall_swamp_cT1", "obs_mtn_tall_swamp_cT2", "obs_mtn_tall_swamp_cT3",
  "obs_mtn_tall_swamp_cT4", "obs_mtn_tall_swamp_cT5", "obs_mtn_tall_swamp_cT6",
  "obs_mtn_tall_swamp_m_B", "obs_mtn_tall_swamp_m_T", "obs_mtn_tall_swamp_m_cB0",
  "obs_mtn_tall_swamp_m_cB1", "obs_mtn_tall_swamp_m_cB2", "obs_mtn_tall_swamp_m_cB3",
  "obs_mtn_tall_swamp_m_cB4", "obs_mtn_tall_swamp_m_cB5", "obs_mtn_tall_swamp_m_cB6",
  "obs_mtn_tall_swamp_m_cT0", "obs_mtn_tall_swamp_m_cT1", "obs_mtn_tall_swamp_m_cT2",
  "obs_mtn_tall_swamp_m_cT3", "obs_mtn_tall_swamp_m_cT4", "obs_mtn_tall_swamp_m_cT5",
  "obs_mtn_tall_swamp_m_cT6", "obs_mtn_tall_swamp_m_r0", "obs_mtn_tall_swamp_m_r1",
  "obs_mtn_tall_swamp_m_r2", "obs_mtn_tall_swamp_m_r3", "obs_mtn_tall_swamp_m_r4",
  "obs_mtn_tall_swamp_m_r5", "obs_mtn_tall_swamp_m_r6", "obs_mtn_tall_swamp_r0",
  "obs_mtn_tall_swamp_r1", "obs_mtn_tall_swamp_r2", "obs_mtn_tall_swamp_r3",
  "obs_mtn_tall_swamp_r4", "obs_mtn_tall_swamp_r5", "obs_mtn_tall_swamp_r6",
  "obs_mtn_skyreach_a_B", "obs_mtn_skyreach_a_T", "obs_mtn_skyreach_a_cB0", "obs_mtn_skyreach_a_cB1",
  "obs_mtn_skyreach_a_cT0", "obs_mtn_skyreach_a_cT1", "obs_mtn_skyreach_a_m_B", "obs_mtn_skyreach_a_m_T",
  "obs_mtn_skyreach_a_m_cB0", "obs_mtn_skyreach_a_m_cB1", "obs_mtn_skyreach_a_m_cT0",
  "obs_mtn_skyreach_a_m_cT1", "obs_mtn_skyreach_a_m_r0", "obs_mtn_skyreach_a_m_r1",
  "obs_mtn_skyreach_a_r0", "obs_mtn_skyreach_a_r1", "obs_mtn_skyreach_b_B", "obs_mtn_skyreach_b_T",
  "obs_mtn_skyreach_b_cB0", "obs_mtn_skyreach_b_cB1", "obs_mtn_skyreach_b_cB2", "obs_mtn_skyreach_b_cT0",
  "obs_mtn_skyreach_b_cT1", "obs_mtn_skyreach_b_cT2", "obs_mtn_skyreach_b_m_B", "obs_mtn_skyreach_b_m_T",
  "obs_mtn_skyreach_b_m_cB0", "obs_mtn_skyreach_b_m_cB1", "obs_mtn_skyreach_b_m_cB2",
  "obs_mtn_skyreach_b_m_cT0", "obs_mtn_skyreach_b_m_cT1", "obs_mtn_skyreach_b_m_cT2",
  "obs_mtn_skyreach_b_m_r0", "obs_mtn_skyreach_b_m_r1", "obs_mtn_skyreach_b_m_r2", "obs_mtn_skyreach_b_r0",
  "obs_mtn_skyreach_b_r1", "obs_mtn_skyreach_b_r2", "obs_mtn_skyreach_lava_B", "obs_mtn_skyreach_lava_T",
  "obs_mtn_skyreach_lava_cB0", "obs_mtn_skyreach_lava_cB1", "obs_mtn_skyreach_lava_cT0",
  "obs_mtn_skyreach_lava_cT1", "obs_mtn_skyreach_lava_m_B", "obs_mtn_skyreach_lava_m_T",
  "obs_mtn_skyreach_lava_m_cB0", "obs_mtn_skyreach_lava_m_cB1", "obs_mtn_skyreach_lava_m_cT0",
  "obs_mtn_skyreach_lava_m_cT1", "obs_mtn_skyreach_lava_m_r0", "obs_mtn_skyreach_lava_m_r1",
  "obs_mtn_skyreach_lava_r0", "obs_mtn_skyreach_lava_r1", "obs_mtn_skyreach_snow_B",
  "obs_mtn_skyreach_snow_T", "obs_mtn_skyreach_snow_cB0", "obs_mtn_skyreach_snow_cB1",
  "obs_mtn_skyreach_snow_cT0", "obs_mtn_skyreach_snow_cT1", "obs_mtn_skyreach_snow_m_B",
  "obs_mtn_skyreach_snow_m_T", "obs_mtn_skyreach_snow_m_cB0", "obs_mtn_skyreach_snow_m_cB1",
  "obs_mtn_skyreach_snow_m_cT0", "obs_mtn_skyreach_snow_m_cT1", "obs_mtn_skyreach_snow_m_r0",
  "obs_mtn_skyreach_snow_m_r1", "obs_mtn_skyreach_snow_r0", "obs_mtn_skyreach_snow_r1",
  "obs_mtn_skyreach_swamp_B", "obs_mtn_skyreach_swamp_T", "obs_mtn_skyreach_swamp_cB0",
  "obs_mtn_skyreach_swamp_cB1", "obs_mtn_skyreach_swamp_cT0", "obs_mtn_skyreach_swamp_cT1",
  "obs_mtn_skyreach_swamp_m_B", "obs_mtn_skyreach_swamp_m_T", "obs_mtn_skyreach_swamp_m_cB0",
  "obs_mtn_skyreach_swamp_m_cB1", "obs_mtn_skyreach_swamp_m_cT0", "obs_mtn_skyreach_swamp_m_cT1",
  "obs_mtn_skyreach_swamp_m_r0", "obs_mtn_skyreach_swamp_m_r1", "obs_mtn_skyreach_swamp_r0",
  "obs_mtn_skyreach_swamp_r1",
  // range-kit:end
  // The cones themselves. Nothing places them any more, but a saved map or a
  // hand-authored scenario may still name one, and `terrainOk` is the rule
  // that decides whether it may stand where it says it stands.
  "obs_mountain_peak", "obs_mountain_peak_snow",
  "obs_mountain_ridge", "obs_mountain_ridge_snow",
  "obs_mountain_cliff", "obs_mountain_spur",
]);

/**
 * Scenery by terrain. The first list blocks and is placed as a real object; the
 * second is a decal that never enters `GameState` at all - a def with kind
 * "obstacle" and `blocksMovement: false` is decoration by definition, because
 * the kernel refuses entry to every object of kind "obstacle" whatever its move
 * cost says.
 */
export interface Scenery {
  readonly blocking: readonly string[];
  readonly decals: readonly string[];
}

export const SCENERY: Readonly<Record<TerrainKind, Scenery>> = Object.freeze({
  grass: {
    blocking: [
      "obs_apple_tree", "obs_birch", "obs_birch_grove", "obs_bush",
      "obs_fence", "obs_forest_edge", "obs_forest_mixed", "obs_forest_oak_dense",
      "obs_hazel_bush", "obs_hedgerow", "obs_hollow_log",
      // The one-tile pieces of the range kit (game/tools/range-kit.py). They
      // are what a wood or a range looks like where it has thinned to a single
      // clump, so they scatter where the old 2x2 stands read as a slab.
      "obs_wood_clump_oak", "obs_wood_clump_pine", "obs_mtn_knoll",
      "obs_ivy_trunk", "obs_maple", "obs_oak", "obs_rocks", "obs_sapling",
      "obs_signpost", "obs_stone_bench", "obs_stump", "obs_watchfire",
      "obs_wildrose", "obs_willow", "obs_woodpile"
    ],
    decals: [
      "obs_anthill", "obs_beehive", "obs_clover_patch", "obs_flower_ring",
      "obs_flowers", "obs_forest_ferns", "obs_molehill", "obs_mushroom",
      "obs_pebbles", "obs_puddle", "obs_rabbit_burrow", "obs_spider_web",
      "obs_tall_grass", "obs_toadstool_ring"
    ],
  },
  dirt: {
    blocking: [
      "obs_broken_cart", "obs_burnt_stump", "obs_deadbush", "obs_deadtree",
      "obs_dry_well", "obs_fence", "obs_gate_post", "obs_grave_cross",
      "obs_gravestones", "obs_oak", "obs_rocks", "obs_rotting_fence",
      "obs_ruined_arch", "obs_ruined_column", "obs_ruined_wall",
      "obs_scree", "obs_signpost", "obs_stump",
      "obs_wood_clump_oak", "obs_mtn_knoll"
    ],
    decals: [
      "obs_bones", "obs_clover_patch", "obs_dry_riverbed", "obs_molehill",
      "obs_nettle_patch", "obs_pebbles", "obs_puddle", "obs_rubble_pile",
      "obs_tall_grass"
    ],
  },
  sand: {
    blocking: [
      "obs_cactus", "obs_cactus_cluster", "obs_desert_rock",
      "obs_desert_shrub", "obs_dock_post", "obs_mesa_rock", "obs_palm",
      "obs_palm_cluster", "obs_rock_arch", "obs_sandstone_pillar",
      "obs_scree"
    ],
    decals: [
      "obs_camel_bones", "obs_desert_bones", "obs_dry_riverbed",
      "obs_dune", "obs_fishing_net", "obs_oasis_reeds", "obs_pebbles",
      "obs_salt_crust", "obs_sand_ripple", "obs_tumbleweed"
    ],
  },
  snow: {
    blocking: [
      "obs_cypress", "obs_forest_pine_dense", "obs_forest_pine_snow",
      "obs_frozen_falls", "obs_ice_spikes", "obs_iceberg",
      "obs_iceblock", "obs_icicle_rock", "obs_pine", "obs_pine_cluster",
      "obs_snow_boulder", "obs_snow_cairn", "obs_snow_fence",
      "obs_snow_stump", "obs_snowpine",
      "obs_wood_clump_pine", "obs_mtn_knoll_snow"
    ],
    decals: [
      "obs_bones", "obs_frost_bush", "obs_frozen_pond",
      "obs_glacier_crack", "obs_ice_floe", "obs_pebbles", "obs_snow_drift",
      "obs_snow_hummock"
    ],
  },
  swamp: {
    blocking: [
      "obs_bamboo", "obs_cypress", "obs_dead_reed", "obs_deadfall",
      "obs_ivy_trunk", "obs_mangrove_root", "obs_shrub_dense",
      "obs_sunken_stump", "obs_swamp_log", "obs_swamp_shrine",
      "obs_thorn_hedge", "obs_vine_tangle", "obs_willow"
    ],
    decals: [
      "obs_bog_bubbles", "obs_bog_pool", "obs_glow_moss",
      "obs_jungle_fern", "obs_leech_pool", "obs_lily_pads",
      "obs_marsh_grass", "obs_nettle_patch", "obs_peat_mound",
      "obs_reed_bed", "obs_reeds", "obs_spider_web", "obs_swamp_vine",
      "obs_toadstool_ring"
    ],
  },
  rough: {
    blocking: [
      "obs_basalt_column", "obs_boulder", "obs_deadtree", "obs_hedgerow",
      "obs_moss_rock", "obs_pine", "obs_rock_arch", "obs_rocks",
      "obs_root_arch", "obs_ruined_column", "obs_scree", "obs_shrub_dense",
      "obs_thorn_hedge", "obs_mtn_knoll", "obs_wood_clump_pine"
    ],
    decals: [
      "obs_bones", "obs_crystal_vein", "obs_molehill", "obs_ore_vein",
      "obs_pebbles", "obs_rubble_pile", "obs_tall_grass"
    ],
  },
  lava: {
    blocking: [
      "obs_abandoned_forge", "obs_basalt_column", "obs_boulder",
      "obs_burnt_stump", "obs_charcoal_kiln", "obs_cinder_cone",
      "obs_lava_spire", "obs_obsidian_shard", "obs_scorched_tree"
    ],
    decals: [
      "obs_ash_drift", "obs_ash_mound", "obs_bones", "obs_ember_pile",
      "obs_gas_vent", "obs_lava_pool", "obs_lavacrack", "obs_lavarock",
      "obs_magma_crack", "obs_smoking_vent", "obs_sulfur_crust"
    ],
  },
  cavern: {
    blocking: [
      "obs_boulder", "obs_cave_column", "obs_crystal_formation",
      "obs_ice_spikes", "obs_moss_rock", "obs_ruined_column",
      "obs_stalactite", "obs_stalagmite"
    ],
    decals: [
      "obs_bat_roost", "obs_cave_mushroom", "obs_cave_pool",
      "obs_crystal_vein", "obs_glow_moss", "obs_ore_vein", "obs_pebbles",
      "obs_shelf_fungus", "obs_spider_web"
    ],
  },
  water: {
    blocking: [],
    decals: ["obs_coral", "obs_lily_pads", "obs_fishing_net", "obs_ice_floe"],
  },
  rock: { blocking: [], decals: [] },
  // Upland moor: the standing stone and the cairn are its own; the boulders,
  // pines and scree are borrowed from rough and snow, which is right - a
  // boulder is a boulder, and a terrain whose every prop is unique reads as a
  // theme park rather than as a place.
  highlands: {
    blocking: [
      "obs_standing_stone", "obs_moor_cairn", "obs_boulder", "obs_moss_rock",
      "obs_forest_edge", "obs_pine", "obs_rocks", "obs_scree", "obs_shrub_dense",
      "obs_stump", "obs_thorn_hedge", "obs_wildrose"
    ],
    decals: [
      "obs_heather_patch", "obs_clover_patch", "obs_forest_ferns",
      "obs_molehill", "obs_pebbles", "obs_tall_grass"
    ],
  },
  // Poisoned barrens: slag and a dead boiler of its own, everything else drawn
  // from the burnt and the dry.
  wasteland: {
    blocking: [
      "obs_slag_heap", "obs_rusted_boiler", "obs_basalt_column",
      "obs_burnt_stump", "obs_deadtree", "obs_obsidian_shard", "obs_rocks",
      "obs_ruined_wall", "obs_scorched_tree", "obs_scree"
    ],
    decals: [
      "obs_tar_seep", "obs_ash_drift", "obs_bones", "obs_dry_riverbed",
      "obs_pebbles", "obs_rubble_pile", "obs_sulfur_crust"
    ],
  },
});

/** Elemental field decals; they are scattered thinly wherever they suit. */
export const FIELD_DECALS: Readonly<Record<TerrainKind, readonly string[]>> = Object.freeze({
  grass: ["field_nature", "field_holy"],
  dirt: ["field_shadow"],
  sand: ["field_lightning"],
  snow: ["field_ice"],
  swamp: ["field_poison", "field_nature"],
  rough: ["field_lightning", "field_arcane"],
  lava: ["field_fire"],
  cavern: ["field_shadow", "field_arcane"],
  water: [],
  rock: [],
  highlands: ["field_nature", "field_lightning"],
  wasteland: ["field_poison", "field_shadow"],
});

/** River-mouth decals, keyed by the river kind that reaches the sea. */
export const DELTA_DECAL: Readonly<Record<string, string>> = Object.freeze({
  water: "delta_water_mouth",
  ice: "delta_ice_mouth",
  lava: "delta_lava_mouth",
  mud: "delta_mud_mouth",
});

// -------------------------------------------------------------- underground

/**
 * The second level's own tables.
 *
 * The classic's underground is not a darker copy of the surface: it is where the
 * rare-resource mines live, where the banks are worth the walk, and where the
 * guards are a tier past what the same distance buys above ground. Wood and
 * peat are surface economies and are deliberately absent.
 */
export const UNDERGROUND_MINES: readonly string[] = Object.freeze([
  "mine_crystal", "mine_gem", "mine_sulfur", "mine_alchemist",
  "mine_obsidian_quarry", "mine_gold", "mine_ore",
]);

export const UNDERGROUND_DWELLINGS: readonly string[] = Object.freeze([
  "dwl_cinder_forge", "dwl_bone_pit", "dwl_crypt", "dwl_rook_roost",
  "dwl_sentinel_works", "dwl_serpent_pool", "dwl_tempter_gate", "dwl_stonebeard_hall",
  "dwl_shriek_cliff", "dwl_watcher_sanctum",
]);

export const UNDERGROUND_BANKS: readonly string[] = Object.freeze([
  "bank_crypt", "bank_stonegaze_lair", "bank_derelict_tower", "bank_serpent_vault",
  "bank_wyrm_hoard", "bank_smuggler_cave", "bank_ruins", "bank_red_tower",
  "bld_sand_tomb",
]);

/**
 * Cave-floor terrains, listed with their weight: cavern is the ground the
 * underground is made of, rough is a rubble floor and lava is a hot chamber.
 * Every pair here is `plausible`, so a chamber may open straight onto another
 * without the seam pass having anything to bridge.
 */
export const UNDERGROUND_FLOORS: readonly TerrainKind[] = Object.freeze([
  "cavern", "cavern", "cavern", "cavern", "rough", "rough", "lava",
]);
